import {
  ChangeDetectionStrategy,
  Component,
  signal,
  computed,
  Input,
  Output,
  EventEmitter,
  ChangeDetectorRef,
  inject,
  ViewChild,
  ElementRef,
  OnInit,
} from '@angular/core';
import {
  MemberFormControlPipe,
  MainModuleControlPipe,
  ChildModuleControlPipe,
  ModuleSelectedCountPipe,
  ModuleIndeterminatePipe,
  MemberSubtitlePipe,
} from './drawer-invite-team-member.pipes';
import {
  AsyncPipe,
  CommonModule,
  NgFor,
} from '@angular/common';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  Validators,
  ReactiveFormsModule,
  FormsModule,
  FormArray,
  FormBuilder,
} from '@angular/forms';
import {
  TuiRepeatTimes,
  TuiDay,
  TuiTime,
  TuiIdentityMatcher,
  TuiValidationError,
} from '@taiga-ui/cdk';
import {
  TuiButton,
  TuiLink,
  TuiPopup,
  TuiScrollbar,
  TuiTitle,
  TuiError,
  TuiGroup,
  TuiIcon,
  TuiLabel,
  TuiTextfield,
  TuiNotification,
  TuiDataList,
  TuiLoader,
} from '@taiga-ui/core';
import {
  TuiBadge,
  TuiBadgeNotification,
  TuiDrawer,
  TuiTabs,
  TuiBlock,
  TuiCheckbox,
  TuiChevron,
  TuiDataListWrapper,
  TuiFieldErrorPipe,
  TuiInputNumber,
  TuiPassword,
  TuiRadio,
  TuiSelect,
  TuiSegmented,
  TuiTooltip,
  tuiInputPhoneInternationalOptionsProvider,
  TuiFilterByInputPipe,
  TuiStringifyContentPipe,
  TuiSwitch,
  TuiRadioList,
  TuiAvatar,
  TuiMultiSelect,
  TuiInputChip,
  tuiValidationErrorsProvider,
  TuiButtonClose,
} from '@taiga-ui/kit';
import {
  TuiInputDateModule,
  TuiInputModule,
  TuiInputPhoneModule,
  TuiInputSliderModule,
  TuiInputTimeModule,
  TuiTextfieldControllerModule,
  TuiMultiSelectModule,
} from '@taiga-ui/legacy';
import {
  TuiHeader,
  TuiNavigation,
  TuiForm,
} from '@taiga-ui/layout';
import {
  TuiCurrency,
  TuiCurrencyPipe,
} from '@taiga-ui/addon-commerce';
import type { TuiCountryIsoCode } from '@taiga-ui/i18n';
import { TuiInputPhoneInternational } from '@taiga-ui/experimental';
import { TuiSortCountriesPipe } from '@taiga-ui/kit';

import { getCountries, getCountryCallingCode } from 'libphonenumber-js';
import { defer, forkJoin, Observable, of, Subject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { tap, catchError, map, debounceTime, switchMap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { TuiComboBoxModule } from '@taiga-ui/legacy';
import { CountryRegionService, StateData } from '../../../services/country-region.service';
import { InputHeading } from '../../input-heading/input-heading';
import { ApiService } from '../../../services/api.service';
import { NotificationService } from '../../../services/notification.service';
import { RoleService } from '../../../services/role.service';
import { TuiInitialsPipe } from '@taiga-ui/core';
import { PermissionDisableDirective } from '../../../directives/permission-disable.directive';

// Helper class
class Location {
  constructor(
    public readonly countryName: string,
    public readonly countryCode: string,
    public readonly states: string[] = [],
  ) { }

  toString(): string {
    return this.countryName;
  }
}

@Component({
  standalone: true,
  selector: 'app-drawer-invite-team-member',
  templateUrl: './drawer-invite-team-member.html',
  styleUrl: './drawer-invite-team-member.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    AsyncPipe,
    NgFor,

    // Taiga UI
    TuiButton,
    TuiButtonClose,
    TuiDrawer,
    TuiHeader,
    TuiNavigation,
    TuiPopup,
    TuiTabs,
    TuiTitle,
    TuiChevron,
    TuiDataListWrapper,
    TuiFieldErrorPipe,
    TuiInputNumber,
    TuiPassword,
    TuiRadio,
    TuiSelect,
    TuiForm,
    TuiError,
    TuiIcon,
    TuiLabel,
    TuiTextfield,
    TuiNotification,
    TuiDataList,
    TuiLoader,
    TuiInputDateModule,
    TuiInputModule,
    TuiInputPhoneModule,
    TuiInputTimeModule,
    TuiTextfieldControllerModule,
    TuiInputPhoneInternational,
    TuiSortCountriesPipe,
    TuiComboBoxModule,
    InputHeading,
    TuiRadioList,
    TuiInputPhoneInternational,
    TuiSortCountriesPipe,
    TuiAvatar,
    TuiInitialsPipe,
    TuiMultiSelectModule,
    TuiBadge,
    TuiBadgeNotification,
    TuiSegmented,
    TuiCheckbox,
    TuiStringifyContentPipe,
    TuiFilterByInputPipe,
    TuiMultiSelect,
    TuiInputChip,
    PermissionDisableDirective,
    MemberFormControlPipe,
    MainModuleControlPipe,
    ChildModuleControlPipe,
    ModuleSelectedCountPipe,
    ModuleIndeterminatePipe,
    MemberSubtitlePipe,
  ],
  providers: [
    tuiInputPhoneInternationalOptionsProvider({
      metadata: defer(() =>
        import('libphonenumber-js/max/metadata').then(m => m.default),
      ),
    }),
    tuiValidationErrorsProvider({
      required: 'This field is required',
      email: 'Please enter a valid email address',
      userExistsThisOrg: "User already exists in this organization. You can update this member's access from the Team table.",
      userExistsOtherOrg: 'User belongs to another organization',
      minlength: ({ requiredLength, actualLength }: { requiredLength: number; actualLength: number }) =>
        `Minimum ${requiredLength} characters required (currently ${actualLength})`,
      maxlength: ({ requiredLength, actualLength }: { requiredLength: number; actualLength: number }) =>
        `Maximum ${requiredLength} characters allowed (currently ${actualLength})`,
      custom: (message: string) => message || 'Invalid value',
    }),
  ],
})
export class DrawerInviteTeamMember implements OnInit {
  @Input() showParkingDropdown: boolean = true;
  @Input() showPermissions: boolean = true;
  @Input() stepType: string = 'default' // 'parking-manager' for "Who manages this parking?" step
  @Input() parkingId: string | null = null;
  @Output() teamMembersCreated = new EventEmitter<any>() // Emit created team member data
  protected readonly open = signal(false);
  protected readonly submissionStatus = signal<'idle' | 'submitting' | 'success' | 'error'>('idle');
  protected readonly activeTab = signal<'organization' | 'parking'>('organization');

  private checkExistenceSubject = new Subject<{ value: string, control: AbstractControl }>();
  protected readonly isCheckingExistence = signal(false);
  private existingUsersMap = new Map<string, { id: string; name: string; org_id: string }>();

  // Access types for the dropdown
  protected readonly accessTypes = ['Organization Access', 'Parking Access'];
  protected readonly accessTypeSignal = signal<string>('Organization Access');
  get selectedAccessType(): string {
    return this.accessTypeSignal();
  }
  set selectedAccessType(val: string) {
    this.accessTypeSignal.set(val);
  }

  protected readonly isOrganizationAccess = computed(() => {
    return this.accessTypeSignal() === 'Organization Access';
  });

  protected readonly isParkingAccess = computed(() => {
    return this.accessTypeSignal() === 'Parking Access';
  });

  protected hasControlError(group: AbstractControl, name: string): boolean {
    const control = group.get(name);
    return !!(control && control.invalid && (control.dirty || control.touched));
  }

  protected onAccessTypeChange(name: string): void {
    if (name) {
      this.selectedAccessType = name;
      const value = name === 'Organization Access' ? 'organization' : 'parking';
      this.onTabChange(value as 'organization' | 'parking');
      this.updateMemberValidators();

      this.teamMembers.controls.forEach(control => {
        if (value === 'parking') {
          // Switching to Parking Access — clear email existence errors, keep phone clean
          const emailCtrl = control.get('emailValue');
          if (emailCtrl) {
            const { userExists, userExistsThisOrg, userExistsOtherOrg, ...rest } = emailCtrl.errors || {};
            emailCtrl.setErrors(Object.keys(rest).length ? rest : null);
          }
        } else {
          // Switching to Org Access — clear phone existence errors, keep email clean
          const phoneCtrl = control.get('phoneValue');
          if (phoneCtrl) {
            const { userExists, userExistsThisOrg, userExistsOtherOrg, ...rest } = phoneCtrl.errors || {};
            phoneCtrl.setErrors(Object.keys(rest).length ? rest : null);
          }
        }
      });

      this.cdr.detectChanges();
    }
  }

  /**
   * Dynamically updates validators for all member form groups based on access type
   */
  private updateMemberValidators(): void {
    const isOrg = this.selectedAccessType === 'Organization Access';

    this.teamMembers.controls.forEach(control => {
      const memberGroup = control as FormGroup;
      const emailControl = memberGroup.get('emailValue');
      const phoneControl = memberGroup.get('phoneValue');

      if (isOrg) {
        emailControl?.setValidators([Validators.required, Validators.email]);
        phoneControl?.setValidators([]) // Optional for Org
      } else {
        // Parking Access: both email and phone are optional individually,
        // but at least one must be filled (validated at submit time)
        emailControl?.setValidators([Validators.email]) // Optional but must be valid if provided
        phoneControl?.setValidators([]) // Optional for Parking (at-least-one checked on submit)
      }

      emailControl?.updateValueAndValidity({ emitEvent: false });
      phoneControl?.updateValueAndValidity({ emitEvent: false });
    });
    this.cdr.detectChanges();
  }
  protected readonly apiErrorMessage = signal<string | null>(null);
  protected readonly phoneEmailError = signal<string | null>(null); // Inline error for Parking Access
  // ==== Modules/Permissions selection state (copied/adapted from SubPlanDrawer) ====
  protected moduleVisibilityMap = new Map<string, boolean>();
  protected modulesData: Array<{ id: string; name: string; childModules?: Array<{ id: string; name: string }> }> = [];
  public selectAllModulesIndeterminate = signal(false);

  // Separate form container for module controls
  protected modulesForm: FormGroup = new FormGroup({
    selectAllModulesControl: new FormControl(false),
  });

  get selectAllModulesControl(): FormControl<boolean> {
    return this.modulesForm.get('selectAllModulesControl') as FormControl<boolean>;
  }

  get roleControl(): FormControl {
    return this.teamMembersForm.get('role') as FormControl;
  }

  // Public method to open the drawer
  public openDrawer(): void {
    this.open.set(true);
    this.apiErrorMessage.set(null) // Reset error when opening

    // Initial module load to ensure "All Services" and "All Facilities" are ready
    // This prevents the "0/0" and "0/2" count issue
    this.loadAllModulesForInvite();

    // Set active tab based on context
    if (this.isInParkingView() || (this.parkingFormControl.value && this.shouldShowTabs())) {
      this.activeTab.set('parking');
      this.selectedAccessType = 'Parking Access';
      console.log('️ Drawer opened in Parking mode');
    } else {
      this.activeTab.set('organization');
      this.selectedAccessType = 'Organization Access';
      console.log('Drawer opened in Organization mode');
    }

    // Apply validators based on initial access type
    this.updateMemberValidators();

    // Reload team members for autocomplete when drawer opens
    this.loadTeamMembersForAutocomplete();

    // IMMEDIATE SYNC: Set the parking control value right away if we have context
    // This prevents "No parking selected" during the few milliseconds while API is loading
    const initialParkingId = this.parkingId || this.getParkingIdFromLocalStorage();
    if (initialParkingId && this.isInParkingView()) {
      // Set temporary value with ID; it will be enriched with the Name once parkingList loads
      this.parkingFormControl.setValue({ id: initialParkingId, name: 'Loading...' });
      // Skip loading facilities during parking creation wizard — newly created parking has no services yet
      if (this.stepType !== 'parking-manager') {
        this.loadFacilitiesFromAPI(initialParkingId).subscribe();
      }
    }

    // Reload parking list when drawer opens to get latest data
    this.loadParkingListFromAPI().subscribe({
      next: () => {
        console.log('Parking list loaded in openDrawer');
        // Auto-select parking if in parking view
        if (this.stepType === 'parking-manager' || this.isInParkingView()) {
          this.setCurrentParkingAutomatically();

          // Now that parking is auto-selected, reload facilities for THIS parking
          // Skip during parking creation wizard — newly created parking has no services yet
          const targetParkingId = this.getParkingIdFromLocalStorage();
          if (targetParkingId && this.stepType !== 'parking-manager') {
            console.log('️ Loading facilities for auto-selected parking:', targetParkingId);
            this.loadFacilitiesFromAPI(targetParkingId).subscribe();
          }
        }
      }
    });

    try {
      console.log('[InviteDrawer] openDrawer called');
    } catch { }

    // Set default phone country from organization location
    this.setDefaultCountryFromOrganization();
  }

  // Public method to open the drawer and pre-fill form for editing
  public openDrawerWithMember(member: any): void {
    this.open.set(true);
    this.apiErrorMessage.set(null) // Reset error state on open

    console.log('EDIT MODE - Member Data:', member);

    // Clear saved members array for edit mode
    this.savedMembers = [];

    // Set edit mode flags FIRST
    this.isEditing = true;
    this.editingIndex = 0;
    this.editingMemberId = member.id || member.user_id || null;

    console.log('EDIT MODE ACTIVATED - Member ID:', this.editingMemberId);

    // Patch the form with the member's data
    const currentMember = this.teamMembers.at(0) as FormGroup;
    currentMember.patchValue({
      nameValue: member.name || member.firstName || '',
      roleGenderValue: member.gender ? { name: member.gender } : null,
      phoneValue: member.phone || '',
      emailValue: member.email || '',
      passwordValue: '', // Do not pre-fill password for security
    });

    // Remove required validator from password field in edit mode (password is optional)
    const passwordControl = currentMember.get('passwordValue');
    if (passwordControl) {
      passwordControl.setValidators([Validators.minLength(8)]) // Only minLength, no required
      passwordControl.updateValueAndValidity();
    }

    // Set phone value for display
    this.phoneValue = member.phone || '';

    // Pre-fill role
    if (member.role_id || member.role) {
      const roleToSet = this.roleList.find((r: any) =>
        r.id === member.role_id || r.name === member.role
      );
      if (roleToSet) {
        console.log('Setting role:', roleToSet);
        this.teamMembersForm.patchValue({ role: roleToSet });
        this.selectedRole = roleToSet;
      } else {
        // Store it as selectedRole so once roleList loads we can resolve it
        this.selectedRole = member.role ? { name: member.role } : { id: member.role_id };
      }
    }

    // Get parking IDs from member data - check multiple possible sources
    let parkingIds: string[] = [];

    // First check parkings array (most common format)
    if (member.parkings && Array.isArray(member.parkings) && member.parkings.length > 0) {
      parkingIds = member.parkings.map((p: any) => p?.id).filter((id: string) => !!id);
      console.log('Found parkings from parkings array:', parkingIds);
    }

    // Fallback/Supplement from parkingIds array
    if (member.parkingIds && Array.isArray(member.parkingIds) && member.parkingIds.length > 0) {
      const additionalIds = member.parkingIds.filter((id: string) => !!id && !parkingIds.includes(id));
      parkingIds = [...parkingIds, ...additionalIds];
      console.log('Found additional parking IDs from parkingIds array:', additionalIds);
    }

    // Fallback from single parkingId field
    if (member.parkingId && !parkingIds.includes(member.parkingId)) {
      parkingIds.push(member.parkingId);
      console.log('Found parking from parkingId field:', member.parkingId);
    }

    console.log('Final parking IDs to use:', parkingIds);
    console.log('Member parking data:', {
      parkingId: member.parkingId,
      parkingIds: member.parkingIds,
      parkings: member.parkings
    });

    // Set access type immediately if we found parkings
    if (parkingIds.length > 0) {
      this.selectedAccessType = 'Parking Access';
    } else {
      this.selectedAccessType = 'Organization Access';
    }

    // Reload parking list when drawer opens to get latest data, then pre-fill parkings
    this.loadParkingListFromAPI().subscribe({
      next: () => {
        // Parking list is now loaded, pre-fill parkings
        if (parkingIds.length > 0) {
          const selectedParkings = this.parkingList.filter((p: any) =>
            parkingIds.includes(p.id) || parkingIds.includes(String(p.id))
          );

          if (selectedParkings.length > 0) {
            // Set as array for multi-select, or single object if only one and not multi-select view
            this.parkingFormControl.setValue(selectedParkings);
            this.cdr.detectChanges();
          } else {
            console.warn('️ No matching parkings found in list. Looking for:', parkingIds);
          }
        }
      },
      error: (error) => {
        console.error('Error loading parking list:', error);
      }
    });

    // Load facilities for the member's parkings, then pre-fill service access
    // Use member's parking IDs if available, otherwise use current parking ID from local storage
    const targetParkingIds = parkingIds.length > 0 ? parkingIds : this.getParkingIdFromLocalStorage();

    if (targetParkingIds && (Array.isArray(targetParkingIds) ? targetParkingIds.length > 0 : true)) {
      // Load facilities for the correct parkings
      this.loadFacilitiesFromAPI(targetParkingIds).subscribe({
        next: () => {
          // Facilities are now loaded, pre-fill service access
          this.prefillServiceAccess(member);
        },
        error: (error) => {
          // Still try to prefill service access even if facilities load fails
          this.prefillServiceAccess(member);
        }
      });
    } else {
      // No parking IDs available, try to prefill service access anyway
      this.prefillServiceAccess(member);
    }

    console.log('Form patched with member data');
  }

  // Helper method to prefill service access values
  private prefillServiceAccess(member: any): void {
    let rawServiceAccess = member.service_access;
    if (typeof rawServiceAccess === 'string') {
      try {
        rawServiceAccess = JSON.parse(rawServiceAccess);
      } catch (e) {
        rawServiceAccess = null;
      }
    }
    if (rawServiceAccess && !Array.isArray(rawServiceAccess) && typeof rawServiceAccess === 'object') {
      rawServiceAccess = [rawServiceAccess];
    }
    if (!rawServiceAccess || !Array.isArray(rawServiceAccess) || rawServiceAccess.length === 0) {
      return;
    }

    const serviceAccess = rawServiceAccess[0];

    // Set vehicle access
    if (serviceAccess.vehicle !== undefined) {
      try {
        const vehicleControl = this.getChildModuleControl('all_services', 'vehicle_parking');
        vehicleControl.setValue(serviceAccess.vehicle, { emitEvent: false });
      } catch (error) {
        console.error('Error setting vehicle access:', error);
      }
    }

    // Set passes access
    if (serviceAccess.passes !== undefined) {
      try {
        const passesControl = this.getChildModuleControl('all_services', 'parking_passes');
        passesControl.setValue(serviceAccess.passes, { emitEvent: false });
        console.log('Passes access set to:', serviceAccess.passes);
      } catch (error) {
        console.error('Error setting passes access:', error);
      }
    }

    // Update All Services parent checkbox if both vehicle and passes are selected
    const allServicesModule = this.modulesData.find((m: any) => m.id === 'all_services');
    if (allServicesModule && allServicesModule.childModules) {
      const vehicleControl = this.getChildModuleControl('all_services', 'vehicle_parking');
      const passesControl = this.getChildModuleControl('all_services', 'parking_passes');
      const allSelected = vehicleControl.value === true && passesControl.value === true;
      const allServicesMainControl = this.getMainModuleControl('all_services');
      allServicesMainControl.setValue(allSelected, { emitEvent: false });
      console.log('All Services parent checkbox set to:', allSelected);
    }

    // Set facility access
    if (serviceAccess.facility) {
      const allFacilitiesModule = this.modulesData.find((m: any) => m.id === 'All Facilities');
      const isAll = serviceAccess.facility === true || (Array.isArray(serviceAccess.facility) && (serviceAccess.facility.includes('all') || serviceAccess.facility.includes('*')));

      if (allFacilitiesModule && allFacilitiesModule.childModules && allFacilitiesModule.childModules.length > 0) {
        if (isAll) {
          allFacilitiesModule.childModules.forEach((facility: any) => {
            try {
              const control = this.getChildModuleControl('All Facilities', facility.id);
              control.setValue(true, { emitEvent: false });
            } catch (e) { }
          });
          const allFacilitiesMainControl = this.getMainModuleControl('All Facilities');
          allFacilitiesMainControl.setValue(true, { emitEvent: false });
          console.log('All Facilities parent checkbox set to: true (wildcard)');
        } else if (Array.isArray(serviceAccess.facility) && serviceAccess.facility.length > 0) {
          serviceAccess.facility.forEach((facilityId: string) => {
            try {
              const control = this.getChildModuleControl('All Facilities', facilityId);
              control.setValue(true, { emitEvent: false });
              console.log('Facility access set for:', facilityId);
            } catch (error) {
              console.error('Error setting facility access for', facilityId, ':', error);
            }
          });

          // Update All Facilities parent checkbox if all facilities are selected
          const allFacilitiesSelected = allFacilitiesModule.childModules.every((facility: any) => {
            const control = this.getChildModuleControl('All Facilities', facility.id);
            return control.value === true;
          });
          const allFacilitiesMainControl = this.getMainModuleControl('All Facilities');
          allFacilitiesMainControl.setValue(allFacilitiesSelected, { emitEvent: false });
          console.log('All Facilities parent checkbox set to:', allFacilitiesSelected);
        }
      } else {
        console.warn('️ Facilities module not found or no facilities loaded yet');
      }
    }

    // Update module states after setting all values
    this.updateSelectAllState();
    this.cdr.detectChanges();
  }

  // Debug: log when phone control gains focus / clicked and whether overlay appears
  public onPhoneFocus(): void {
    try {
      console.log('[InviteDrawer] Phone input focus');
      // Attempt to open the dropdown programmatically via keyboard interaction
      setTimeout(() => {
        this.dispatchArrowDownOnPhone();
        const overlay = document.querySelector('.cdk-overlay-container .cdk-overlay-pane');
        console.log('[InviteDrawer] Overlay after focus', overlay);
      }, 0);
    } catch { }
  }

  public onPhoneClick(event: MouseEvent): void {
    try {
      console.log('[InviteDrawer] Phone input click', event);
      setTimeout(() => {
        const overlay = document.querySelector('.cdk-overlay-container .cdk-overlay-pane');
        console.log('[InviteDrawer] Overlay after click', overlay);
      }, 0);
    } catch { }
  }

  @ViewChild('phoneInputRef')
  private phoneInputRef?: ElementRef<HTMLInputElement>;

  private dispatchArrowDownOnPhone(): void {
    try {
      const el = this.phoneInputRef?.nativeElement;
      if (!el) return;
      const ev = new KeyboardEvent('keydown', { key: 'ArrowDown', bubbles: true });
      el.dispatchEvent(ev);
    } catch { }
  }

  /**
   * Sets default phone country based on current organization's country
   */
  private setDefaultCountryFromOrganization(): void {
    try {
      const orgId = this.apiService.getCurrentOrganizationId();
      if (!orgId) return;
      this.http.get<any>(`${this.apiService['baseUrl']}/organization/${orgId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      }).subscribe({
        next: (resp: any) => {
          let org = resp?.data || resp;
          const countryName = org?.address?.country || '';
          if (countryName) {
            // Map country name to ISO code using CountryRegionService data
            this.countryRegionService.getAllCountries().subscribe(list => {
              const match = (list || []).find(c =>
                (c.name || '').toLowerCase() === String(countryName).toLowerCase() ||
                (c.code || '').toLowerCase() === String(countryName).toLowerCase()
              );
              const code = (match?.code || '') as TuiCountryIsoCode;
              if (code && (getCountries() as string[]).includes(code)) {
                this.countryIsoCode = code;
                this.countries = [code];
                this.cdr.detectChanges();
              }
            });
          }
        },
        error: () => { }
      });
    } catch { }
  }

  // Public method to close the drawer
  public closeDrawer(): void {
    this.open.set(false);
    // Reset all form values when drawer closes
    this.resetAllFormValues();
  }

  // Reset all form values to initial state
  private resetAllFormValues(): void {
    // Reset form fields
    const currentMember = this.teamMembers.at(0) as FormGroup;
    currentMember.reset();

    // Clear touched/dirty state on member fields
    currentMember.markAsUntouched();
    currentMember.markAsPristine();

    // Restore password field validators (required + minLength) when exiting edit mode
    const passwordControl = currentMember.get('passwordValue');
    if (passwordControl) {
      passwordControl.setValidators([Validators.required, Validators.minLength(8)]);
      passwordControl.updateValueAndValidity({ emitEvent: false });
    }

    // Reset role and parking
    this.teamMembersForm.patchValue({ role: null });
    // Clear touched/dirty on role so "This field is required" doesn't show on fresh open
    this.roleControl?.markAsUntouched();
    this.roleControl?.markAsPristine();
    this.parkingFormControl.setValue(null);

    // Reset phone value
    this.phoneValue = '';

    // Reset saved members
    this.savedMembers = [];

    // Reset editing state
    this.isEditing = false;
    this.editingIndex = -1;
    this.editingMemberId = null;
    this.selectedRole = null;

    // Reset permissions
    this.resetPermissionsSelection();

    // Reset submission status and inline errors
    this.submissionStatus.set('idle');
    this.phoneEmailError.set(null);
  }

  // Public method to reset the form to blank for new member
  public resetFormToBlank(): void {
    const currentMember = this.teamMembers.at(0) as FormGroup;
    currentMember.patchValue({
      nameValue: '',
      roleGenderValue: null,
      phoneValue: '',
      emailValue: '',
      passwordValue: ''
    });

    // Restore password field validators (required + minLength) when exiting edit mode
    const passwordControl = currentMember.get('passwordValue');
    if (passwordControl) {
      passwordControl.setValidators([Validators.required, Validators.minLength(8)]);
      passwordControl.updateValueAndValidity();
    }

    // Clear touched/dirty state so validation errors don't bleed into the fresh form
    currentMember.markAsUntouched();
    currentMember.markAsPristine();

    this.teamMembersForm.patchValue({ role: null });
    // Also clear touched state on role control
    this.roleControl?.markAsUntouched();
    this.roleControl?.markAsPristine();

    this.parkingFormControl.setValue(null);
    this.phoneValue = '';
    this.phoneEmailError.set(null); // Clear inline error
    this.resetPermissionsSelection();
    this.isEditing = false;
    this.editingIndex = -1;
    this.editingMemberId = null // Reset editing member ID
  }

  // Method to add a member and always reset edit state and form
  protected onAddAnother(): void {
    // Save the current member first before resetting the form
    this.saveMember();
    this.editingMemberId = null;
  }

  // Form array for multiple team members
  protected teamMembersForm: FormGroup;


  // Getter for parking form control
  get parkingFormControl(): FormControl {
    return this.teamMembersForm?.get('parking') as FormControl;
  }

  protected parking: any[] = [
    {
      name: 'Parking 1',
      id: 1,
    },
    {
      name: 'Parking 2',
      id: 2,
    },
    {
      name: 'Parking 3',
      id: 3,
    },
  ];

  // Getter for team members form array
  get teamMembers(): FormArray {
    return this.teamMembersForm.get('members') as FormArray;
  }

  // Method to create a new member form group
  private createMemberFormGroup(): FormGroup {
    const isOrg = this.selectedAccessType === 'Organization Access';

    return this.fb.group({
      nameValue: ['', [Validators.minLength(2)]],
      roleGenderValue: [null],
      // Parking Access: phone is optional (at-least-one-of email/phone checked at submit time)
      phoneValue: ['', []], // No required for either access type here; org email handles required below
      emailValue: ['', isOrg ? [Validators.required, Validators.email] : [Validators.email]],
      passwordValue: ['', [Validators.required, Validators.minLength(8)]],
    });
  }

  // Method to add a new team member
  protected addTeamMember(): void {
    this.teamMembers.push(this.createMemberFormGroup());
  }

  // Method to remove a team member
  protected removeTeamMember(index: number): void {
    if (this.teamMembers.length > 1) {
      this.teamMembers.removeAt(index);
    }
  }

  // Method to get form group at specific index
  protected getMemberFormGroup(index: number): FormGroup {
    return this.teamMembers.at(index) as FormGroup;
  }

  // Helper to get FormControl for template bindings to avoid type errors
  protected getControl(group: FormGroup, key: string): FormControl {
    return group.get(key) as FormControl;
  }

  // Method to check if form has substantial data to show preview
  protected hasFormData(): boolean {
    const firstMember = this.teamMembers.at(0) as FormGroup;
    if (!firstMember) return false;

    const name = firstMember.get('nameValue')?.value;
    const email = firstMember.get('emailValue')?.value;
    const phone = firstMember.get('phoneValue')?.value;
    const gender = firstMember.get('roleGenderValue')?.value;

    // Only show preview if at least name and email are filled
    return !!(name && email);
  }

  // Array to store saved members data
  protected savedMembers: any[] = [];

  // Public getter to access saved members
  public getSavedMembers(): any[] {
    return this.savedMembers;
  }

  // Track if we're currently in edit mode
  protected isEditing = false;
  protected editingIndex = -1;
  protected editingMemberId: string | null = null // Store member ID when editing

  // Store the selected role to ensure it's maintained
  protected selectedRole: any = null;

  private _cachedFilteredRoles: any[] | null = null;
  protected readonly roleValueSignal = signal<any>(null);
  protected readonly selectedParkingsSignal = signal<any>(null);



  // Method to save current member data and clear form
  protected saveMember(): void {
    const memberData = this.getMemberDataFromForm();

    if (memberData) {
      if (this.isEditing && this.editingIndex >= 0 && this.savedMembers[this.editingIndex]) {
        // Update existing member
        this.savedMembers[this.editingIndex] = {
          ...memberData,
          id: this.savedMembers[this.editingIndex]?.id || Date.now()
        };
      } else {
        // Add new member
        this.savedMembers.push(memberData);
        console.log('Saved member with context:', { role: memberData.role?.name, parking: memberData.parking?.name });
      }

      // Clear the form for next member
      this.resetFormToBlank();

      // Reset editing state
      this.isEditing = false;
      this.editingIndex = -1;
      this.editingMemberId = null;

      // Show success message briefly
      this.submissionStatus.set('success');
      this.notificationService.showSuccess('Member saved to list!');
      setTimeout(() => {
        this.submissionStatus.set('idle');
      }, 1000);

    } else {
      this.notificationService.showError('Please fill name and email first.');
      this.submissionStatus.set('error');
      setTimeout(() => {
        this.submissionStatus.set('idle');
      }, 1000);
    }
  }

  // Create a data object from current form state without resetting the form
  private getMemberDataFromForm(): any {
    const currentMember = this.teamMembers.at(0) as FormGroup;
    if (!currentMember) return null;

    // Use getRawValue to include disabled fields (like email/phone in edit mode)
    const memberData = currentMember.getRawValue();

    if (currentMember.invalid) return null;

    const name = currentMember.get('nameValue')?.value;
    const email = currentMember.get('emailValue')?.value;

    const role = this.teamMembersForm.get('role')?.value;
    let selectedParking = this.parkingFormControl.value;

    // In parking-manager mode, ensure parking is set from localStorage if not in form control
    if (this.stepType === 'parking-manager' && !selectedParking) {
      const parkingIdFromStorage = this.getParkingIdFromLocalStorage();
      if (parkingIdFromStorage) {
        const parkingFromList = this.parkingList.find((p: any) => p.id === parkingIdFromStorage);
        if (parkingFromList) {
          selectedParking = parkingFromList;
        }
      }
    }

    const selectedPermissions = this.getSelectedPermissions();
    const currentRole = role || this.selectedRole;
    const currentParking = selectedParking || (this.isInParkingView() ? { id: this.getParkingIdFromLocalStorage(), name: 'New park' } : null);

    return {
      ...memberData,
      genderValue: memberData.roleGenderValue?.name || memberData.genderValue || 'Male',
      role: currentRole,
      parking: currentParking,
      permissions: selectedPermissions,
      id: Date.now()
    };
  }

  // Method to save current edited member
  protected saveCurrentEdit(): void {
    const currentMember = this.teamMembers.at(0) as FormGroup;

    if (currentMember.valid && this.isEditing && this.editingIndex >= 0 && this.savedMembers[this.editingIndex]) {
      const memberData = currentMember.getRawValue();

      // Update the existing member in place (only basic data, role/permissions come from form on submit)
      this.savedMembers[this.editingIndex] = {
        ...memberData,
        genderValue: memberData.roleGenderValue?.name || memberData.genderValue || 'Male',
        id: this.savedMembers[this.editingIndex]?.id || Date.now() // keep the same ID or generate new
      };

    }
  }

  // Method to check if current form has data to save
  protected canSaveMember(): boolean {
    const currentMember = this.teamMembers.at(0) as FormGroup;
    if (!currentMember) return false;

    const name = currentMember.get('nameValue')?.value;
    const email = currentMember.get('emailValue')?.value;
    const phone = currentMember.get('phoneValue')?.value;
    const gender = currentMember.get('roleGenderValue')?.value;
    const password = currentMember.get('passwordValue')?.value;

    // Check role from the main form, not from the member form
    const role = this.teamMembersForm.get('role')?.value;

    // Enable button if current member form is valid
    return currentMember.valid;
  }

  // Method to check if form is ready for submission
  protected canSubmitForm(): boolean {
    // Check if we have saved members or current form data
    const hasSavedMembers = this.savedMembers.length > 0;
    const hasCurrentData = this.canSaveMember();
    const hasRole = !!this.teamMembersForm.get('role')?.value;

    // Parking is optional - no validation needed
    return (hasSavedMembers || hasCurrentData) && hasRole;
  }

  // Method to edit a saved member
  protected editMember(index: number): void {
    // If we're already editing, save the current edit first
    if (this.isEditing && this.editingIndex >= 0 && this.editingIndex !== index) {
      this.saveCurrentEdit();
    }

    const memberToEdit = this.savedMembers[index];
    if (memberToEdit) {
      // Fill the form with member data
      const currentMember = this.teamMembers.at(0) as FormGroup;
      currentMember.patchValue({
        nameValue: memberToEdit.nameValue,
        roleGenderValue: memberToEdit.roleGenderValue,
        phoneValue: memberToEdit.phoneValue,
        emailValue: memberToEdit.emailValue,
        passwordValue: memberToEdit.passwordValue
      });

      // Set role in main form (use stored role if available, otherwise keep current form role)
      if (memberToEdit.role) {
        const roleToSet = this.roleList.find((r: any) =>
          r.id === memberToEdit.role.id || r.name === memberToEdit.role.name || r.name === memberToEdit.role
        );
        this.teamMembersForm.patchValue({
          role: roleToSet || memberToEdit.role
        });
        if (roleToSet) {
          this.selectedRole = roleToSet;
        }
      }
      // Note: If role is not stored, keep the current form role (for shared role scenario)

      // Set parking in parking form
      if (memberToEdit.parking) {
        this.parkingFormControl.setValue(memberToEdit.parking);
      }

      // Set phone value for display
      this.phoneValue = memberToEdit.phoneValue;

      // Set editing state
      this.isEditing = true;
      this.editingIndex = index;
    }
  }


  protected readonly genderList = [
    {
      name: 'Male',
    },
    {
      name: 'Female',
    },
  ];

  protected readonly parkingListSignal = signal<any[]>([]);
  get parkingList(): any[] {
    return this.parkingListSignal();
  }
  set parkingList(val: any[]) {
    this.parkingListSignal.set(val);
  }
  protected loadingParking = signal(false);

  // Getter for parking list with debugging
  get parkingListForDisplay(): any[] {
    return this.parkingList;
  }

  // Filter out already selected parkings from the dropdown list using computed signal
  protected readonly getFilteredParkingList = computed(() => {
    const parkings = this.parkingListSignal();
    const selectedParkings = this.selectedParkingsSignal() || this.parkingFormControl?.value || [];
    const selectedIds = Array.isArray(selectedParkings)
      ? selectedParkings.map((p: any) => p.id)
      : (selectedParkings?.id ? [selectedParkings.id] : []);

    return parkings.filter(parking => !selectedIds.includes(parking.id));
  });




  protected readonly orgLocation = [
    new Location('India', 'IN', ['Delhi', 'Maharashtra', 'Karnataka', 'Tamil Nadu']),
    new Location('United States', 'US', ['California', 'Texas', 'New York']),
    new Location('Australia', 'AU', ['New South Wales', 'Victoria', 'Queensland']),
    new Location('Canada', 'CA', ['Ontario', 'Quebec', 'British Columbia']),
    new Location('Germany', 'DE', ['Bavaria', 'Berlin', 'Saxony']),
  ];

  protected readonly timeZone = [
    "(GMT+05:30) India Standard Time (Asia/Kolkata)",
    "(GMT-12:00) International Date Line West (Etc/GMT+12)",
    "(GMT-11:00) Coordinated Universal Time-11 (Etc/GMT+11)",
    "(GMT-10:00) Hawaii-Aleutian Standard Time (Pacific/Honolulu)",
    "(GMT-09:00) Alaska Standard Time (America/Anchorage)",
    "(GMT-08:00) Pacific Standard Time (America/Los_Angeles)",
    "(GMT-07:00) Mountain Standard Time (America/Denver)",
    "(GMT-06:00) Central Standard Time (America/Chicago)",
    "(GMT-05:00) Eastern Standard Time (America/New_York)",
    "(GMT-04:00) Atlantic Standard Time (America/Halifax)",
    "(GMT-03:30) Newfoundland Standard Time (America/St_Johns)",
    "(GMT-03:00) Argentina Standard Time (America/Argentina/Buenos_Aires)",
    "(GMT-02:00) Coordinated Universal Time-2 (Etc/GMT+2)",
    "(GMT-01:00) Azores Standard Time (Atlantic/Azores)",
    "(GMT+00:00) Greenwich Mean Time (Europe/London)",
    "(GMT+01:00) Central European Standard Time (Europe/Berlin)",
    "(GMT+02:00) Eastern European Standard Time (Europe/Kiev)",
    "(GMT+03:00) Moscow Standard Time (Europe/Moscow)",
    "(GMT+03:30) Iran Standard Time (Asia/Tehran)",
    "(GMT+04:00) Gulf Standard Time (Asia/Dubai)",
    "(GMT+04:30) Afghanistan Time (Asia/Kabul)",
    "(GMT+05:00) Pakistan Standard Time (Asia/Karachi)",
    "(GMT+05:45) Nepal Time (Asia/Kathmandu)",
    "(GMT+06:00) Bangladesh Standard Time (Asia/Dhaka)",
    "(GMT+06:30) Myanmar Time (Asia/Yangon)",
    "(GMT+07:00) Indochina Time (Asia/Bangkok)",
    "(GMT+08:00) China Standard Time (Asia/Shanghai)",
    "(GMT+09:00) Japan Standard Time (Asia/Tokyo)",
    "(GMT+09:30) Australian Central Standard Time (Australia/Adelaide)",
    "(GMT+10:00) Australian Eastern Standard Time (Australia/Sydney)",
    "(GMT+11:00) Solomon Islands Time (Pacific/Guadalcanal)",
    "(GMT+12:00) New Zealand Standard Time (Pacific/Auckland)",
    "(GMT+13:00) Phoenix Island Time (Pacific/Enderbury)",
    "(GMT+14:00) Line Islands Time (Pacific/Kiritimati)",
  ];

  protected currency = [
    "INR - Indian Rupee",
    "USD - United States Dollar",
    "EUR - Euro",
    "GBP - British Pound Sterling",
    "AUD - Australian Dollar",
    "JPY - Japanese Yen",
    "CAD - Canadian Dollar",
    "CHF - Swiss Franc",
    "CNY - Chinese Yuan",
    "SEK - Swedish Krona",
    "NZD - New Zealand Dollar",
    "SGD - Singapore Dollar",
    "HKD - Hong Kong Dollar",
    "THB - Thai Baht",
    "MYR - Malaysian Ringgit",
    "PHP - Philippine Peso",
    "IDR - Indonesian Rupiah",
    "BRL - Brazilian Real",
    "ZAR - South African Rand",
    "MXN - Mexican Peso",
    "CLP - Chilean Peso",
    "ARS - Argentine Peso",
    "PEN - Peruvian Nuevo Sol",
    "UYU - Uruguayan Peso",
    "BOB - Boliviano",
    "PYG - Paraguayan Guarani",
    "VEF - Venezuelan Bolívar",
    "CRC - Costa Rican Colon",
  ];

  protected language = [
    "English", "Arabic", "French", "German", "Spanish", "Italian",
    "Portuguese", "Russian", "Chinese", "Japanese", "Korean", "Hindi",
  ];

  protected taxName = [
    "GST", "VAT", "PST", "HST", "QST"
  ];

  protected countries: TuiCountryIsoCode[] = getCountries();
  protected countryIsoCode: TuiCountryIsoCode = 'IN';
  protected phoneValue = '';

  // Legacy form - keeping for reference but not used in new implementation
  protected testForm = new FormGroup({
    orgValue: new FormControl('', Validators.required),
    emailValue: new FormControl('', [Validators.required, Validators.email]),
    nameValue: new FormControl('', Validators.required),
    passwordValue: new FormControl('', Validators.required),
    phoneValue: new FormControl('', Validators.required),
    addressValue: new FormControl('', Validators.required),
    timeZoneValue: new FormControl(this.timeZone[0], Validators.required),
    currencyValue: new FormControl(this.currency[0], Validators.required),
    languageValue: new FormControl(this.language[0], Validators.required),
    taxNameValue: new FormControl(this.taxName[0], Validators.required),
    taxIdValue: new FormControl(''),
    personValue: new FormControl<{ name: string; code: string } | null>(null, Validators.required),
    stateValue: new FormControl<{ name: string; code: string } | null>(null, Validators.required),
    taxIdControl: new FormControl(false),
    role: new FormControl(null, Validators.required),
    roleGenderValue: new FormControl(null, Validators.required),
  });

  // New properties for country-based selection
  protected country: { name: string; code: string }[] = [];
  protected statesForSelectedCountry: StateData[] = [];
  protected timezonesForSelectedCountry: string[] = [];
  protected hasMultipleTimezones = false;
  protected currenciesForSelectedCountry: string[] = [];
  protected hasMultipleCurrencies = false;

  // Role list with descriptions - will be populated from API
  protected readonly roleListSignal = signal<any[]>([]);
  get roleList(): any[] {
    return this.roleListSignal();
  }
  set roleList(val: any[]) {
    this.roleListSignal.set(val);
  }
  protected loadingRoles = signal(false);

  // Team members list for email autocomplete
  protected teamMembersList: Array<{ email: string; name?: string; phone?: string }> = [];
  // Store full team member data for auto-fill
  protected fullTeamMembersData: Array<{ email: string; name?: string; phone?: string; firstName?: string; lastName?: string }> = [];
  protected loadingTeamMembers = signal(false);
  protected showSuggestions = signal(false);
  protected currentEmailInput = signal<string>('');

  // Add identityMatcher for tui-multi-select
  protected readonly identityMatcher: TuiIdentityMatcher<any> = (a, b) => {
    if (!a || !b) return false;
    if (a.id !== undefined && b.id !== undefined) {
      return a.id === b.id;
    }
    return a.name === b.name;
  };

  // Provide parking options for the multi-select
  public getGatewaysForDisplay() {
    // Use parkingList from API instead of hardcoded parking array
    return this.parkingList && this.parkingList.length > 0 ? this.parkingList : this.parking;
  }

  // trackBy function for ngFor in parking multi-select
  public trackByCode(index: number, item: any): any {
    return item.id ?? item.code ?? index;
  }

  @ViewChild('scrollContainer', { static: false })
  private scrollContainer?: ElementRef<HTMLElement>;

  @ViewChild('errorNotification', { static: false })
  private errorNotification?: ElementRef<HTMLElement>;

  constructor(
    private countryRegionService: CountryRegionService,
    private fb: FormBuilder,
    private apiService: ApiService,
    private notificationService: NotificationService,
    private roleService: RoleService,
    private http: HttpClient,
    private cdr: ChangeDetectorRef,
    private router: Router
  ) {
    // Subscribe to existence check subject
    this.checkExistenceSubject.pipe(
      tap(() => this.isCheckingExistence.set(true)),
      debounceTime(300),
      switchMap(({ value, control }) => {
        if (!value || value.length < 5 || this.isEditing) {
          this.isCheckingExistence.set(false);
          return of({ exists: false, userOrgId: null, user: null, control, value });
        }
        return this.apiService.checkUserExistence(value).pipe(
          map(res => ({
            exists: !!res.data?.exists,
            userOrgId: res.data?.user?.org_id,
            user: res.data?.user,
            control,
            value
          })),
          catchError(() => of({ exists: false, userOrgId: null, user: null, control, value })),
          tap(() => this.isCheckingExistence.set(false))
        );
      })
    ).subscribe(({ exists, userOrgId, user, control, value }) => {
      this.isCheckingExistence.set(false);
      // Clear previous existence errors
      const currentErrors = control.errors || {};
      const { userExists, userExistsThisOrg, userExistsOtherOrg, ...remainingErrors } = currentErrors;

      if (exists && user) {
        // Store existing user details
        const key = String(value).trim().toLowerCase();
        const cleanKey = key.replace(/\s+/g, '');
        const entry = {
          id: user.id || user._id || '',
          name: user.name || '',
          org_id: userOrgId || ''
        };
        this.existingUsersMap.set(key, entry);
        this.existingUsersMap.set(cleanKey, entry);

        // Do not auto-fill name on user match


        const currentUser = this.apiService.getCurrentUser();
        const currentOrgId = currentUser?.organization_id || currentUser?.org_id;

        if (userOrgId && userOrgId === currentOrgId) {
          // Same organization - show error so user knows this person already exists
          control.setErrors({ ...remainingErrors, userExistsThisOrg: true });
          control.markAsTouched();
        } else if (userOrgId && userOrgId !== currentOrgId) {
          // Belongs to a different organization - show error
          control.setErrors({ ...remainingErrors, userExistsOtherOrg: true });
          control.markAsTouched();
        } else {
          // User exists but has no organization assigned (org_id is null) - can be invited to this org!
          control.setErrors(Object.keys(remainingErrors).length ? remainingErrors : null);
        }
      } else {
        control.setErrors(Object.keys(remainingErrors).length ? remainingErrors : null);
      }
      this.cdr.detectChanges();
    });
    // Initialize country list from API
    this.countryRegionService.getAllCountries().subscribe(countries => {
      this.country = countries;
    });

    // Load roles from API
    this.loadRolesFromAPI();

    // Load parking list from API
    this.loadParkingListFromAPI().subscribe();

    // Load team members for email autocomplete
    this.loadTeamMembersForAutocomplete();

    // Initialize team members form array with role control
    this.teamMembersForm = this.fb.group({
      members: this.fb.array([this.createMemberFormGroup()]),
      role: [null, Validators.required], // Add role control to main form
      parking: [[]], // Changed to array for multi-select
    });

    // Subscribe to email value changes to convert object to string
    this.teamMembers.at(0).get('emailValue')?.valueChanges.subscribe(value => {
      if (value && typeof value === 'object' && value.email) {
        // Convert object to string when selected from dropdown
        this.teamMembers.at(0).get('emailValue')?.setValue(value.email, { emitEvent: false });
      }
      // Trigger change detection for autocomplete
      this.cdr.detectChanges();

      // Only check email existence in Organization Access mode
      if (!this.isOrganizationAccess()) return;
      const emailControl = this.teamMembers.at(0).get('emailValue');
      const emailStr = typeof value === 'string' ? value : (value?.email || '');
      if (emailControl && emailStr && emailStr.length > 3 && !this.isEditing) {
        this.checkExistenceSubject.next({ value: emailStr, control: emailControl });
      }
    });

    // Subscribe to phone value changes — only check existence in Parking Access mode
    this.teamMembers.at(0).get('phoneValue')?.valueChanges.subscribe(value => {
      if (!this.isParkingAccess()) return;
      const phoneControl = this.teamMembers.at(0).get('phoneValue');
      if (phoneControl && value && String(value).length > 5 && !this.isEditing) {
        this.checkExistenceSubject.next({ value: String(value), control: phoneControl });
      }
    });

    // Subscribe to role changes
    this.teamMembersForm.get('role')?.valueChanges.subscribe(selectedRole => {
      this.roleValueSignal.set(selectedRole);
      if (selectedRole) {
        this.onRoleSelectionChange(selectedRole);
      }
    });

    // Subscribe to parking selection changes - reload facilities when parking is selected
    this.parkingFormControl.valueChanges.subscribe((selectedParking: any) => {
      this.selectedParkingsSignal.set(selectedParking);
      const parkings = Array.isArray(selectedParking) ? selectedParking : (selectedParking ? [selectedParking] : []);

      if (parkings.length > 0) {
        const parkingIds = parkings.map((p: any) => p.id);
        console.log('Parking(s) selected, loading facilities for:', parkingIds);

        // Switch to parking tab if tabs are enabled (check admin and route, not parking state)
        const isAdmin = this.isAdminUser();
        const currentUrl = this.router.url;
        const isTeamPage = currentUrl.includes('/settings/team/');
        const shouldShowTabsNow = isAdmin && isTeamPage && !this.hasExistingParkingId();

        if (shouldShowTabsNow) {
          setTimeout(() => {
            this.activeTab.set('parking');
            this.cdr.detectChanges();
          }, 0);
        }
        // Reset permissions before loading new facilities
        this.resetPermissionsSelection();
        // Load facilities for the selected parkings (skip during parking creation wizard)
        if (this.stepType !== 'parking-manager') {
          this.loadFacilitiesFromAPI(parkingIds).subscribe();
        }
      } else {
        // If parking is cleared, switch back to organization tab if tabs are enabled
        const isAdmin = this.isAdminUser();
        const currentUrl = this.router.url;
        const isTeamPage = currentUrl.includes('/settings/team/');
        const shouldShowTabsNow = isAdmin && isTeamPage && !this.hasExistingParkingId();

        if (shouldShowTabsNow) {
          setTimeout(() => {
            this.activeTab.set('organization');
            this.cdr.detectChanges();
          }, 0);
        }
        // Clear facilities and reset permissions
        const allFacilitiesModule = this.modulesData.find((m: any) => m.id === 'All Facilities');
        if (allFacilitiesModule) {
          allFacilitiesModule.childModules = [];
          this.initializeModuleControls();
          this.setupChildModuleListeners();
          this.resetPermissionsSelection();
          this.cdr.detectChanges();
        }
      }
    });

    // Initialize module visibility and controls — moved to ngOnInit so @Input() stepType is available
    // this.loadAllModulesForInvite() is now called in ngOnInit
    this.selectAllModulesControl.valueChanges.subscribe((checked: boolean | null) => {
      if (checked === true) {
        this.modulesData.forEach((module: any) => {
          const mainControl = this.getMainModuleControl(module.id);
          mainControl.setValue(true);
          module.childModules?.forEach((child: any) => {
            const control = this.getChildModuleControl(module.id, child.id);
            control.setValue(true);
          });
        });
      } else if (checked === false) {
        this.modulesData.forEach((module: any) => {
          const mainControl = this.getMainModuleControl(module.id);
          mainControl.setValue(false);
          module.childModules?.forEach((child: any) => {
            const control = this.getChildModuleControl(module.id, child.id);
            control.setValue(false);
          });
        });
      }
      this.updateSelectAllState();
    });
  }

  ngOnInit(): void {
    // loadAllModulesForInvite is called here (not in constructor) so that
    // @Input() stepType is already set — prevents the facility services API
    // from firing during parking creation (assign-manager wizard step)
    this.loadAllModulesForInvite();
  }


  protected readonly stringify = (item: { name: string; code: string }): string =>
    `${item.name}`;

  protected readonly stringifyState = (item: { name: string; code: string }): string =>
    `${item.name}`;

  // Stringify function for email autocomplete
  protected readonly stringifyEmail = (item: { email: string; name?: string } | string | null): string => {
    if (!item) return '';
    if (typeof item === 'string') return item;
    return item.email || '';
  };

  // Helper method to extract email string from form control value (handles both string and object)
  // If user typed their own email (not from suggestions), return that exact value
  private getEmailString(value: any): string {
    if (!value) return '';
    // If it's already a string (user typed or selected), return as-is
    if (typeof value === 'string') return value.trim();
    // If it's an object (from suggestion selection), extract email
    if (value && typeof value === 'object' && value.email) return value.email.trim();
    // Fallback: convert to string
    return String(value).trim();
  }

  // Get filtered team members list - only show when user is typing
  protected getFilteredTeamMembersList(memberForm: FormGroup | any): Array<{ email: string; name?: string }> {
    if (!memberForm || !memberForm.get) {
      return [];
    }
    const emailControl = memberForm.get('emailValue');
    if (!emailControl) {
      return [];
    }
    const emailValue = emailControl.value;

    // Handle both string and object values
    let searchText = '';
    if (typeof emailValue === 'string') {
      searchText = emailValue;
    } else if (emailValue && typeof emailValue === 'object' && emailValue.email) {
      searchText = emailValue.email;
    }

    // Only show suggestions if user has typed at least 1 character
    if (!searchText || searchText.trim().length === 0) {
      return [];
    }

    // Return full list - tuiFilterByInput will filter it based on the control value
    return this.teamMembersList || [];
  }

  // Show email suggestions dropdown
  protected showEmailSuggestions(memberForm: FormGroup | any): boolean {
    if (this.isEditing || !this.showSuggestions()) {
      return false;
    }
    if (!memberForm || !memberForm.get) {
      return false;
    }
    const emailControl = memberForm.get('emailValue');
    if (!emailControl) {
      return false;
    }
    const emailValue = emailControl.value;
    const searchText = typeof emailValue === 'string' ? emailValue : (emailValue?.email || '');
    return !!(searchText && searchText.trim().length > 0);
  }

  // Get filtered email suggestions list
  protected getEmailSuggestionsList(memberForm: FormGroup | any): Array<{ email: string; name?: string }> {
    if (!memberForm || !memberForm.get) {
      return [];
    }
    const emailControl = memberForm.get('emailValue');
    if (!emailControl) {
      return [];
    }
    const emailValue = emailControl.value;
    const searchText = typeof emailValue === 'string' ? emailValue : (emailValue?.email || '');

    if (!searchText || searchText.trim().length === 0) {
      return [];
    }

    const lowerSearch = searchText.toLowerCase().trim();
    return this.teamMembersList.filter(member =>
      member.email.toLowerCase().includes(lowerSearch)
    ).slice(0, 10) // Limit to 10 suggestions
  }

  // Handle email input
  protected onEmailInput(event: Event, memberForm: FormGroup | any): void {
    if (this.isEditing) return;
    const input = event.target as HTMLInputElement;
    const value = (input.value || '').trim();
    this.currentEmailInput.set(value);
    this.showSuggestions.set(value.length > 0);

    // Clear inline at-least-one error when user starts typing
    if (this.phoneEmailError()) this.phoneEmailError.set(null);

    const control = memberForm.get('emailValue');
    if (!control) return;

    if (value.length > 3) {
      this.checkExistenceSubject.next({ value, control });
    } else {
      const currentErrors = control.errors || {};
      const { userExists, userExistsThisOrg, userExistsOtherOrg, ...remainingErrors } = currentErrors;
      control.setErrors(Object.keys(remainingErrors).length ? remainingErrors : null);
    }

    this.cdr.detectChanges();
  }

  protected getCallingCode(): string {
    try {
      if (this.countryIsoCode) {
        return getCountryCallingCode(this.countryIsoCode as any);
      }
    } catch (e) {}
    return '91';
  }

  protected getNationalDigits(value: string): string {
    const digits = (value || '').replace(/\D/g, '');
    const callingCode = this.getCallingCode();
    if (callingCode && digits.startsWith(callingCode)) {
      return digits.slice(callingCode.length);
    }
    return digits;
  }

  protected getMaxPhoneDigits(): number {
    return 10;
  }

  protected onPhoneKeyDown(event: KeyboardEvent): void {
    // Always allow navigation, deletion, shortcuts
    if (
      event.key === 'Backspace' ||
      event.key === 'Delete' ||
      event.key === 'ArrowLeft' ||
      event.key === 'ArrowRight' ||
      event.key === 'ArrowUp' ||
      event.key === 'ArrowDown' ||
      event.key === 'Tab' ||
      event.key === 'Enter' ||
      event.key === 'Escape' ||
      event.key === 'Home' ||
      event.key === 'End' ||
      event.ctrlKey ||
      event.metaKey
    ) {
      return;
    }

    // Only allow digits (0-9)
    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
      return;
    }

    const input = event.target as HTMLInputElement;
    const selStart = input.selectionStart ?? 0;
    const selEnd = input.selectionEnd ?? 0;
    // If text is selected, typing will overwrite selection
    if (selEnd > selStart) {
      return;
    }

    const currentVal = input.value || '';
    const nationalDigits = this.getNationalDigits(currentVal);
    const maxDigits = this.getMaxPhoneDigits();

    if (nationalDigits.length >= maxDigits) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  protected onPhonePaste(event: ClipboardEvent, memberForm: FormGroup | any): void {
    const pastedText = event.clipboardData?.getData('text') || '';
    const pastedDigits = pastedText.replace(/\D/g, '');
    if (!pastedDigits) return;

    event.preventDefault();
    const input = event.target as HTMLInputElement;
    const currentVal = input.value || '';
    const callingCode = this.getCallingCode();
    const existingNational = this.getNationalDigits(currentVal);
    const maxDigits = this.getMaxPhoneDigits();

    const selStart = input.selectionStart ?? 0;
    const selEnd = input.selectionEnd ?? 0;
    const hasSelection = selEnd > selStart;

    let digitsToAppend = pastedDigits;
    if (callingCode && digitsToAppend.startsWith(callingCode)) {
      digitsToAppend = digitsToAppend.slice(callingCode.length);
    }

    const baseNational = hasSelection ? '' : existingNational;
    const finalNational = (baseNational + digitsToAppend).slice(0, maxDigits);
    const finalVal = callingCode ? `+${callingCode} ${finalNational}` : finalNational;

    input.value = finalVal;
    const control = memberForm?.get ? memberForm.get('phoneValue') : null;
    if (control) {
      control.setValue(finalVal);
    }
    this.onPhoneInput({ target: input } as any, memberForm);
  }

  // Handle phone input
  protected onPhoneInput(event: Event, memberForm: FormGroup | any): void {
    const input = event.target as HTMLInputElement;
    let value = (input.value || '').trim();

    // Clear inline at-least-one error when user starts typing
    if (this.phoneEmailError()) this.phoneEmailError.set(null);

    const control = memberForm?.get ? memberForm.get('phoneValue') : null;

    // Enforce max national digits limit (10 digits)
    const callingCode = this.getCallingCode();
    const nationalDigits = this.getNationalDigits(value);
    const maxDigits = this.getMaxPhoneDigits();

    if (nationalDigits.length > maxDigits) {
      const truncated = nationalDigits.slice(0, maxDigits);
      const formatted = callingCode ? `+${callingCode} ${truncated}` : truncated;
      input.value = formatted;
      if (control) {
        control.setValue(formatted, { emitEvent: false });
      }
      value = formatted;
    }

    if (!control) return;

    // Check phone existence when at least 5 digits entered in any mode
    const digits = value.replace(/\D/g, '');
    if (digits.length >= 5 || value.length >= 5) {
      this.checkExistenceSubject.next({ value, control });
    } else {
      const currentErrors = control.errors || {};
      const { userExists, userExistsThisOrg, userExistsOtherOrg, ...remainingErrors } = currentErrors;
      control.setErrors(Object.keys(remainingErrors).length ? remainingErrors : null);
    }
  }

  // Handle email focus
  protected onEmailFocus(memberForm: FormGroup | any): void {
    if (this.isEditing) return;
    if (!memberForm || !memberForm.get) {
      return;
    }
    const emailControl = memberForm.get('emailValue');
    if (!emailControl) {
      return;
    }
    const emailValue = emailControl.value;
    const searchText = typeof emailValue === 'string' ? emailValue : (emailValue?.email || '');
    if (searchText && searchText.trim().length > 0) {
      this.showSuggestions.set(true);
      this.cdr.detectChanges();
    }
  }

  // Handle email blur - hide suggestions after a delay
  protected onEmailBlur(): void {
    setTimeout(() => {
      this.showSuggestions.set(false);
      this.cdr.detectChanges();
    }, 200);
  }

  // Select email suggestion and auto-fill name and phone
  protected selectEmailSuggestion(email: string, memberForm: FormGroup | any): void {
    if (!memberForm || !memberForm.get) {
      return;
    }

    // Find the full member data by email
    const memberData = this.fullTeamMembersData.find(m =>
      m.email.toLowerCase() === email.toLowerCase()
    );

    // Set email
    const emailControl = memberForm.get('emailValue');
    if (emailControl) {
      emailControl.setValue(email);
    }

    // Auto-fill name if available
    if (memberData && memberData.name) {
      const nameControl = memberForm.get('nameValue');
      if (nameControl) {
        nameControl.setValue(memberData.name);
      }
    }

    // Auto-fill phone if available
    if (memberData && memberData.phone) {
      const phoneControl = memberForm.get('phoneValue');
      if (phoneControl) {
        phoneControl.setValue(memberData.phone);
        // Also update the phoneValue property for display
        this.phoneValue = memberData.phone;
      }
    }

    this.showSuggestions.set(false);
    this.cdr.detectChanges();
  }

  // Get email suggestions - simplified method for template
  protected getEmailSuggestions(memberForm: FormGroup | any): Array<{ email: string; name?: string }> {
    if (!memberForm || !memberForm.get) {
      return [];
    }
    const emailControl = memberForm.get('emailValue');
    if (!emailControl) {
      return [];
    }
    const emailValue = emailControl.value;
    const searchText = typeof emailValue === 'string' ? emailValue : (emailValue?.email || '');

    // Only show suggestions if user has typed at least 1 character
    if (!searchText || (typeof searchText === 'string' && searchText.trim().length === 0)) {
      return [];
    }

    // Return full list - tuiFilterByInput will filter it based on the control value
    return this.teamMembersList;
  }

  /**
   * Load team members from API for email autocomplete
   */
  private loadTeamMembersForAutocomplete(): void {
    this.loadingTeamMembers.set(true);

    const orgId = this.apiService.getCurrentOrganizationId();
    if (!orgId) {
      console.warn('️ No organization ID found for loading team members');
      this.loadingTeamMembers.set(false);
      return;
    }

    this.apiService.getAllOrganizationUsers(orgId).subscribe({
      next: (response: any) => {
        // Handle different response formats
        let members: any[] = [];

        if (Array.isArray(response)) {
          members = response;
        } else if (response && response.data && Array.isArray(response.data)) {
          members = response.data;
        } else if (response && response.users && Array.isArray(response.users)) {
          members = response.users;
        }

        // Extract unique emails from team members and store full data
        const emailSet = new Set<string>();
        this.fullTeamMembersData = members
          .filter((member: any) => {
            const email = member.email || member.user_email || '';
            if (email && !emailSet.has(email.toLowerCase())) {
              emailSet.add(email.toLowerCase());
              return true;
            }
            return false;
          })
          .map((member: any) => ({
            email: member.email || member.user_email || '',
            name: member.name || member.firstName || member.user_name || '',
            phone: member.phone || member.phone_number || '',
            firstName: member.firstName || '',
            lastName: member.lastName || ''
          }))
          .filter((item: any) => item.email) // Remove items without email

        // Create simplified list for suggestions display
        this.teamMembersList = this.fullTeamMembersData.map(member => ({
          email: member.email,
          name: member.name,
          phone: member.phone
        }));

        console.log('Loaded team members for autocomplete:', this.teamMembersList.length);
        this.loadingTeamMembers.set(false);
        this.cdr.detectChanges();
      },
      error: (error: any) => {
        console.error('Error loading team members for autocomplete:', error);
        this.loadingTeamMembers.set(false);
        this.teamMembersList = [];
        this.fullTeamMembersData = [];
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Load roles from API
   */
  private loadRolesFromAPI(): void {
    this.loadingRoles.set(true);

    // Use the current API service base URL instead of hardcoded old URL
    const url = `${this.apiService['baseUrl']}/parking-access-module/role/all`;

    this.http.get(url, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      }
    }).subscribe({
      next: (response: any) => {
        if (response.success && response.data) {
          // Transform API data to match our expected format and filter out "Super Admin"
          this.roleList = response.data
            .filter((role: any) => (role.name || role.role_name) !== 'Super Admin')
            .map((role: any) => ({
              id: role.id, // Store the actual role ID from API
              name: role.name || role.role_name,
              icon: this.getRoleIcon(role.name || role.role_name),
              color: 'var(--tui-chart-categorical-00)',
              backgroundColor: 'var(--tui-service-selection-background)',
              gender: 'male',
              description: role.description || role.role_description || 'Role description not available'
            }));

        } else if (Array.isArray(response)) {
          // Direct array response and filter out "Super Admin"
          this.roleList = response
            .filter((role: any) => (role.name || role.role_name) !== 'Super Admin')
            .map((role: any) => ({
              id: role.id, // Store the actual role ID from API
              name: role.name || role.role_name,
              icon: this.getRoleIcon(role.name || role.role_name),
              color: 'var(--tui-chart-categorical-00)',
              backgroundColor: 'var(--tui-service-selection-background)',
              gender: 'male',
              description: role.description || role.role_description || 'Role description not available'
            }));

        }

        // Resolve pending selected role to exact reference in newly loaded roleList
        const currentRoleVal = this.teamMembersForm?.get('role')?.value || this.selectedRole;
        if (currentRoleVal) {
          const roleToSet = this.roleList.find((r: any) =>
            r.id === currentRoleVal.id || r.name === currentRoleVal.name || r.name === currentRoleVal
          );
          if (roleToSet) {
            console.log('Resolving pending role to loaded reference:', roleToSet);
            this.teamMembersForm.patchValue({ role: roleToSet });
            this.selectedRole = roleToSet;
          }
        }

        // Reset cache so list re-evaluates with new role list
        this._cachedFilteredRoles = null;

        this.loadingRoles.set(false);
      },
      error: (error: any) => {

        this.loadingRoles.set(false);

        // Fallback to default roles if API fails - using actual API data
        this.roleList = [
          {
            id: 'a3d27252-b527-4602-b177-df86e0571fe6',
            name: 'Admin',
            icon: 'assets/images/icons/modules/Settings.svg',
            color: 'var(--tui-chart-categorical-00)',
            backgroundColor: 'var(--tui-service-selection-background)',
            gender: 'male',
            description: 'Full access to the entire organization - can manage parking lots, users, settings, billing, and create custom roles. Ideal for business owners or IT leads.'
          },
          {
            id: 'e4f38264-d124-462b-b289-fd97f15c47e7',
            name: 'Manager',
            icon: 'assets/images/icons/modules/Team.svg',
            color: 'var(--tui-chart-categorical-00)',
            backgroundColor: 'var(--tui-service-selection-background)',
            gender: 'male',
            description: 'Manages one or more parking lots. Can view reports, configure facilities and pricing, and oversee operators. Best suited for local managers or site-level supervisors.'
          },
          {
            id: '22e7dabd-c113-451a-a10a-c64fee5b96da',
            name: 'Vendor',
            icon: 'assets/images/icons/modules/Organisations.svg',
            color: 'var(--tui-chart-categorical-00)',
            backgroundColor: 'var(--tui-service-selection-background)',
            gender: 'male',
            description: 'Manages one or more parking lots. Can view reports, configure facilities and pricing, and oversee operators. Best suited for local vendors or site-level supervisors.'
          },
          {
            id: '453e2732-e778-452c-ac2a-edaae08fa97f',
            name: 'Operator',
            icon: 'assets/images/icons/modules/Vehicle Activity.svg',
            color: 'var(--tui-chart-categorical-00)',
            backgroundColor: 'var(--tui-service-selection-background)',
            gender: 'male',
            description: 'Day-to-day parking operator. Can log vehicle entries/exits, apply passes, and manage facility usage like valet or EV charging. No access to settings or billing.'
          },
          {
            id: 'b22da3b1-1854-49b9-8438-43ae6f44565a',
            name: 'Accountant',
            icon: 'assets/images/icons/modules/billing & invoices.svg',
            color: 'var(--tui-chart-categorical-00)',
            backgroundColor: 'var(--tui-service-selection-background)',
            gender: 'male',
            description: 'Can view and download transactions, monitor revenue reports, and track financial performance. No access to parking logs or team management.'
          },
          {
            id: 'c413d430-4939-4365-9e34-53712a39e785',
            name: 'Viewer',
            icon: 'assets/images/icons/modules/Reports.svg',
            color: 'var(--tui-chart-categorical-00)',
            backgroundColor: 'var(--tui-service-selection-background)',
            gender: 'male',
            description: 'Read-only access to dashboards and reports. Ideal for internal auditors, consultants, or compliance staff.'
          }
        ];

        // Resolve pending selected role to exact reference in newly loaded roleList
        const currentRoleVal = this.teamMembersForm?.get('role')?.value || this.selectedRole;
        if (currentRoleVal) {
          const roleToSet = this.roleList.find((r: any) =>
            r.id === currentRoleVal.id || r.name === currentRoleVal.name || r.name === currentRoleVal
          );
          if (roleToSet) {
            console.log('Resolving pending role to loaded reference:', roleToSet);
            this.teamMembersForm.patchValue({ role: roleToSet });
            this.selectedRole = roleToSet;
          }
        }

        // Reset cache so list re-evaluates with new role list
        this._cachedFilteredRoles = null;
      }
    });
  }

  // Helper method to get appropriate icon for each role
  private getRoleIcon(roleName: string): string {
    const iconMap: { [key: string]: string } = {
      'Admin': 'assets/images/icons/modules/Settings.svg',
      'Manager': 'assets/images/icons/modules/Team.svg',
      'Vendor': 'assets/images/icons/modules/Organisations.svg',
      'Operator': 'assets/images/icons/modules/Vehicle Activity.svg',
      'Accountant': 'assets/images/icons/modules/billing & invoices.svg',
      'Viewer': 'assets/images/icons/modules/Reports.svg',
      'Custom': 'assets/images/icons/modules/Roles.svg'
    };
    return iconMap[roleName] || 'assets/images/icons/modules/Team.svg';
  }

  // ===== Modules loading and control helpers (adapted) =====
  private loadAllModulesForInvite(parkingId?: string | string[]): void {
    // Only initialize modules if we don't already have them to avoid flickering
    if (!this.modulesData || this.modulesData.length === 0) {
      this.modulesData = [
        {
          id: 'all_services',
          name: 'All Services',
          childModules: [
            { id: 'vehicle_parking', name: 'Vehicle Parking' },
            { id: 'parking_passes', name: 'Parking Passes' },
          ],
        },
        {
          id: 'All Facilities',
          name: 'All Facilities',
          childModules: [],
        },
      ];
    }

    // Load facilities from parking detail API
    // If parking ID(s) provided, use them!
    // Skip loading facilities during parking creation wizard (stepType === 'parking-manager')
    // because a newly created parking won't have services yet and the backend may reject the request
    const targetParkingIds = parkingId || this.getParkingIdFromLocalStorage();
    if (targetParkingIds && (Array.isArray(targetParkingIds) ? targetParkingIds.length > 0 : true) && this.stepType !== 'parking-manager') {
      this.loadFacilitiesFromAPI(targetParkingIds).subscribe();
    }

    // Initialize visibility and controls
    this.moduleVisibilityMap.clear();
    this.modulesData.forEach(m => this.moduleVisibilityMap.set(m.id, false));
    this.initializeModuleControls();
    this.setupChildModuleListeners();
    this.updateSelectAllState();
  }

  /**
   * Load facilities from parking detail API
   * Filters services where service_type === "facility"
   * @param parkingId Optional parking ID. If not provided, uses current parking ID
   * @returns Observable that completes when facilities are loaded
   */
  private loadFacilitiesFromAPI(parkingIds?: string | string[]): Observable<any> {
    // Determine target IDs: can be single ID, array of IDs, or fallback to storage
    let targetIds: string[] = [];

    if (Array.isArray(parkingIds)) {
      targetIds = parkingIds.filter((id: string) => !!id);
    } else if (parkingIds) {
      targetIds = [parkingIds];
    } else {
      const storageId = this.getParkingIdFromLocalStorage();
      if (storageId) targetIds = [storageId];
    }

    if (targetIds.length === 0) {
      console.warn('No parking IDs available, skipping facilities load');
      return of(null);
    }

    console.log('️ Loading facilities for parkings:', targetIds);

    // Fetch parking services for all IDs
    const requests = targetIds.map((id: string) =>
      this.apiService.getParkingServices(id, { service_type: 'facility' }).pipe(
        catchError(err => {
          console.error(`Error loading facilities for parking ${id}:`, err);
          return of({ services: [] }) // Continue with other parkings if one fails
        }),
        // Map response to include parking context if needed
        map((response: any) => {
          let services: any[] = [];
          if (response && response.services) {
            services = response.services;
          } else if (response && response.data && response.data.services) {
            services = response.data.services;
          } else if (Array.isArray(response)) {
            services = response;
          }

          // Enrich services with parking info if we have multiple parkings
          if (targetIds.length > 1) {
            // Try to find parking name from current selection
            const currentSelection = this.parkingFormControl.value;
            const parkings = Array.isArray(currentSelection) ? currentSelection : (currentSelection ? [currentSelection] : []);
            const parking = parkings.find((p: any) => String(p.id) === String(id));
            const parkingLabel = parking?.name || 'Parking';

            services = services.map((s: any) => ({
              ...s,
              name: `${s.name} (${parkingLabel})`,
              parkingId: id, // Keep track of which parking this facility belongs to
            }));
          }
          return services;
        })
      )
    );

    return forkJoin(requests).pipe(
      tap({
        next: (allServicesResults: any[][]) => {
          // Flatten all results into a single array
          const allServices = allServicesResults.flat();
          console.log(`Total facilities found across all parkings: ${allServices.length}`);

          // Update All Facilities module with actual facilities
          const allFacilitiesModule = this.modulesData.find((m: any) => m.id === 'All Facilities');
          if (allFacilitiesModule) {
            if (allServices.length > 0) {
              allFacilitiesModule.childModules = allServices.map((facility: any) => ({
                id: facility.id,
                name: facility.name || 'Unnamed Facility',
              }));

              // Re-initialize controls for the new facilities
              this.initializeModuleControls();
              this.setupChildModuleListeners();
            } else {
              console.log('️ No facility services found, keeping the module empty');
              allFacilitiesModule.childModules = [];
            }

            // Trigger change detection to update UI
            this.cdr.detectChanges();
          }
        },
        error: (error: any) => {
          console.error('Critical error in forkJoin loading facilities:', error);
        }
      })
    );
  }

  protected toggleModuleVisibility(moduleId: string): void {
    const current = this.moduleVisibilityMap.get(moduleId) || false;
    this.moduleVisibilityMap.forEach((_, key) => this.moduleVisibilityMap.set(key, false));
    if (!current) this.moduleVisibilityMap.set(moduleId, true);
  }

  protected isModuleVisible(moduleId: string): boolean {
    return this.moduleVisibilityMap.get(moduleId) || false;
  }

  protected getModuleSelectedCount(module: any): number {
    if (!module.childModules) return 0;
    return module.childModules.filter((child: any) => this.getChildModuleControl(module.id, child.id).value).length;
  }

  protected toggleModuleChildren(module: any): void {
    if (!module.childModules) return;
    const count = this.getModuleSelectedCount(module);
    const allSelected = count === module.childModules.length;
    const someSelected = count > 0 && count < module.childModules.length;
    const newValue = someSelected || !allSelected;

    this.getMainModuleControl(module.id).setValue(newValue);
    module.childModules.forEach((child: any) => {
      this.getChildModuleControl(module.id, child.id).setValue(newValue);
    });
    this.updateSelectAllState();
  }

  protected isModuleIndeterminate(module: any): boolean {
    const count = this.getModuleSelectedCount(module);
    return count > 0 && count < (module.childModules?.length || 0);
  }

  protected onChildModuleChange(module: any): void {
    const count = this.getModuleSelectedCount(module);
    const total = module.childModules?.length || 0;
    // Parent is checked only when every child is selected; otherwise false (indeterminate handles partial)
    this.getMainModuleControl(module.id).setValue(total > 0 && count === total, { emitEvent: false });
    this.updateSelectAllState();
  }

  protected getChildModuleControl(moduleId: string, childId: string): FormControl {
    const name = `module_${moduleId}_${childId}`;
    let control = this.modulesForm.get(name) as FormControl;
    if (!control) {
      control = new FormControl(false);
      (this.modulesForm as any).addControl(name, control);
    }
    return control;
  }

  protected getMainModuleControl(moduleId: string): FormControl {
    const name = `module_main_${moduleId}`;
    let control = this.modulesForm.get(name) as FormControl;
    if (!control) {
      control = new FormControl(false);
      (this.modulesForm as any).addControl(name, control);
    }
    return control;
  }

  private initializeModuleControls(): void {
    this.modulesData.forEach((module: any) => {
      const main = `module_main_${module.id}`;
      if (!this.modulesForm.get(main)) (this.modulesForm as any).addControl(main, new FormControl(false));
      module.childModules?.forEach((child: any) => {
        const childName = `module_${module.id}_${child.id}`;
        if (!this.modulesForm.get(childName)) (this.modulesForm as any).addControl(childName, new FormControl(false));
      });
    });
  }

  private setupChildModuleListeners(): void {
    this.modulesData.forEach((module: any) => {
      module.childModules?.forEach((child: any) => {
        const controlName = `module_${module.id}_${child.id}`;
        const ctrl = this.modulesForm.get(controlName);
        ctrl?.valueChanges.subscribe(() => {
          // Keep parent checkbox in sync whenever any child changes
          const count = this.getModuleSelectedCount(module);
          const total = module.childModules?.length || 0;
          this.getMainModuleControl(module.id).setValue(total > 0 && count === total, { emitEvent: false });
          this.updateSelectAllState();
        });
      });
    });
  }

  private updateSelectAllState(): void {
    let total = 0;
    let selected = 0;
    this.modulesData.forEach((module: any) => {
      const mainCtrl = this.getMainModuleControl(module.id);
      total++;
      if (mainCtrl.value) selected++;
      module.childModules?.forEach((child: any) => {
        total++;
        if (this.getChildModuleControl(module.id, child.id).value) selected++;
      });
    });
    const allSelected = total > 0 && selected === total;
    const someSelected = selected > 0 && selected < total;
    this.selectAllModulesIndeterminate.set(someSelected);
    this.selectAllModulesControl.setValue(allSelected, { emitEvent: false });
  }

  // Clear all permission selections (parent and child) and update select-all state
  private resetPermissionsSelection(): void {
    this.modulesData.forEach((module: any) => {
      const mainCtrl = this.getMainModuleControl(module.id);
      mainCtrl.setValue(false);
      module.childModules?.forEach((child: any) => {
        const childCtrl = this.getChildModuleControl(module.id, child.id);
        childCtrl.setValue(false);
      });
    });
    this.selectAllModulesIndeterminate.set(false);
    this.selectAllModulesControl.setValue(false, { emitEvent: false });
  }

  // Build a list of selected permissions from modules and child modules
  private getSelectedPermissions(): Array<{ moduleId: string; moduleName: string; childId?: string; childName?: string }> {
    const selections: Array<{ moduleId: string; moduleName: string; childId?: string; childName?: string }> = [];
    this.modulesData.forEach((module: any) => {
      const mainSelected = this.getMainModuleControl(module.id).value === true;
      if (mainSelected) {
        selections.push({ moduleId: module.id, moduleName: module.name });
      }
      module.childModules?.forEach((child: any) => {
        const childSelected = this.getChildModuleControl(module.id, child.id).value === true;
        if (childSelected) {
          selections.push({ moduleId: module.id, moduleName: module.name, childId: child.id, childName: child.name });
        }
      });
    });
    return selections;
  }

  // Get current service access (vehicle, passes, facilities) for storing with member
  private getCurrentServiceAccess(): { vehicle: boolean; passes: boolean; facility: string[] } {
    let vehicleAccess = false;
    let passesAccess = false;
    const selectedFacilityIds: string[] = [];

    // Get vehicle and passes access
    const allServicesModule = this.modulesData.find((m: any) => m.id === 'all_services');
    if (allServicesModule && allServicesModule.childModules) {
      const vehicleControl = this.getChildModuleControl('all_services', 'vehicle_parking');
      const passesControl = this.getChildModuleControl('all_services', 'parking_passes');
      vehicleAccess = vehicleControl.value === true;
      passesAccess = passesControl.value === true;
    }

    // Get facility access — only the checked facility IDs.
    const allFacilitiesModule = this.modulesData.find((m: any) => m.id === 'All Facilities');
    if (allFacilitiesModule && allFacilitiesModule.childModules) {
      allFacilitiesModule.childModules.forEach((facility: any) => {
        const control = this.getChildModuleControl('All Facilities', facility.id);
        if (control.value === true) {
          selectedFacilityIds.push(facility.id);
        }
      });
    }

    return {
      vehicle: vehicleAccess,
      passes: passesAccess,
      facility: selectedFacilityIds
    };
  }

  // Create a human-readable label for a member's selected services
  // Now uses current form permissions (shared for all members)
  public getPermissionsLabelForMember(member: any): string {
    // Use current form permissions (shared for all members)
    const perms: Array<{ moduleId: string; moduleName: string; childId?: string; childName?: string }> = this.getSelectedPermissions();
    if (!perms.length) {
      return '';
    }
    // Prefer child names; if none, fall back to module names
    const childNames = Array.from(new Set(perms
      .filter(p => !!p.childName)
      .map(p => p.childName as string)));
    const namesToShow = childNames.length
      ? childNames
      : Array.from(new Set(perms.map(p => p.moduleName)));
    return namesToShow.join(', ');
  }

  /**
   * Generates a clean subtitle for a member by joining available info with bullets
   */
  public getMemberSubtitle(member: any): string {
    const parts: string[] = [];

    // 1. Gender/Role Prefix
    const gender = member.roleGenderValue?.name || member.roleGenderValue;
    if (gender) parts.push(gender);

    // 2. Role
    const role = member.role?.name || this.teamMembersForm.get('role')?.value?.name;
    if (role) parts.push(role);

    // 3. Parking
    const parkingVal = member.parking || this.parkingFormControl.value;
    if (parkingVal) {
      if (Array.isArray(parkingVal)) {
        if (parkingVal.length > 0) {
          parts.push(parkingVal.map((p: any) => p.name || p).join(', '));
        }
      } else if (parkingVal.name) {
        parts.push(parkingVal.name);
      } else if (typeof parkingVal === 'string') {
        parts.push(parkingVal);
      }
    }

    // 4. Permissions
    const permissions = this.getPermissionsLabelForMember(member);
    if (permissions) parts.push(permissions);

    // 5. Phone
    if (member.phoneValue) parts.push(member.phoneValue);

    // 6. Email
    if (member.emailValue) parts.push(member.emailValue);

    return parts.join(' • ');
  }

  /**
   * Load parking list from API
   */
  private loadParkingListFromAPI(): Observable<any[]> {
    this.loadingParking.set(true);

    // Get organization ID from localStorage or API service
    const orgId = this.apiService.getCurrentOrganizationId() ||
      sessionStorage.getItem('organization_id');

    if (!orgId) {
      console.warn('️ No organization ID found for loading parking list');
      this.loadingParking.set(false);
      this.parkingList = [];
      this.cdr.markForCheck();
      return of([]);
    }

    console.log('Loading parking list for organization:', orgId);

    return this.apiService.getParkingListByOrg(orgId).pipe(
      tap((response: any) => {
        this.loadingParking.set(false);

        // Handle different response structures
        let parkings: any[] = [];

        if (response && response.data && Array.isArray(response.data)) {
          // Response wrapped in data object
          parkings = response.data;
        } else if (response && response.parkings && Array.isArray(response.parkings)) {
          // Response has parkings array directly
          parkings = response.parkings;
        } else if (Array.isArray(response)) {
          // Direct array response
          parkings = response;
        } else if (response && response.rows && Array.isArray(response.rows)) {
          // Response has rows array (pagination format)
          parkings = response.rows;
        }

        // Transform API data to match our expected format
        this.parkingList = parkings.map((parking: any) => ({
          id: parking.id,
          name: parking.name || parking.parking_name || '-'
        }));

        console.log(`Loaded ${this.parkingList.length} parkings for dropdown:`, this.parkingList);

        // Trigger change detection to update the UI
        this.cdr.markForCheck();

        // Removed direct call to setCurrentParkingAutomatically here 
        // to avoid race conditions; it is now explicitly called in openDrawer
      }),
      catchError((error: any) => {
        console.error('Error loading parking list:', error);
        this.loadingParking.set(false);
        this.parkingList = [];
        this.cdr.markForCheck();
        this.notificationService.showError('Failed to load parking list', 3000);
        return of([]);
      })
    );
  }

  /**
   * Automatically set the current parking based on localStorage
   */
  private setCurrentParkingAutomatically(): void {
    const currentParkingId = this.getParkingIdFromLocalStorage();

    console.log('Setting parking automatically - stepType:', this.stepType, 'currentParkingId:', currentParkingId);

    if (currentParkingId && this.parkingList.length > 0) {
      // Find the parking object that matches the current parking ID
      // Use String() comparison to handle potential type mismatches (string vs number)
      const currentParking = this.parkingList.find(parking => String(parking.id) === String(currentParkingId).trim());

      if (currentParking) {
        // Set the parking form control to the current parking
        this.parkingFormControl.setValue(currentParking);
        console.log('Automatically set current parking:', currentParking);
        // Force change detection to update UI (preview card)
        this.cdr.detectChanges();
      } else {
        console.log('️ Current parking not found in list:', currentParkingId, 'Available IDs:', this.parkingList.map(p => p.id));
      }
    } else {
      console.log('No current parking ID or empty parking list:', currentParkingId, 'List length:', this.parkingList.length);
    }
  }

  protected readonly stringifyGender = (item: { name: string; icon: string; color: string; backgroundColor: string }): string =>
    `${item.name}`;

  protected readonly stringifyParking = (item: { name: string; id: number }): string =>
    `${item.name}`;

  // Method to check if a field has visible errors for team members
  protected hasVisibleErrors(controlName: string, memberIndex: number): boolean {
    const memberForm = this.teamMembers.at(memberIndex) as FormGroup;
    const control = memberForm.get(controlName);
    return !!(control && control.invalid && control.touched);
  }

  /**
   * Helper method to mark all controls in a form group as touched
   * This triggers validation display for all fields
   */
  private markFormGroupTouched(formGroup: FormGroup | FormArray): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);

      if (control instanceof FormControl) {
        control.markAsTouched();
      } else if (control instanceof FormGroup || control instanceof FormArray) {
        this.markFormGroupTouched(control);
      }
    });
  }

  // Form submission method
  protected onSubmit(): void {
    this.apiErrorMessage.set(null) // Reset error state on new submission

    // Mark all form controls as touched to trigger validation display
    this.markFormGroupTouched(this.teamMembersForm);

    // Check if role is selected first - try form control first, then stored value
    let selectedRole = this.teamMembersForm.get('role')?.value;


    // If form control doesn't have the role, use the stored value
    if (!selectedRole && this.selectedRole) {
      selectedRole = this.selectedRole;
    }

    if (!selectedRole) {
      // Set error on the role control instead of showing global toast
      this.roleControl.setErrors({ required: true });
      this.roleControl.markAsTouched();
      this.submissionStatus.set('error');
      setTimeout(() => {
        this.submissionStatus.set('idle');
      }, 3000);
      return;
    }

    // Check if current member form is valid before proceeding
    const firstMember = this.teamMembers.at(0) as FormGroup;

    // Parking Access: at least one of email or phone must be filled (check FIRST before form.invalid)
    if (!this.isEditing && this.isParkingAccess() && this.savedMembers.length === 0) {
      const emailVal = firstMember?.get('emailValue')?.value?.trim();
      const phoneVal = firstMember?.get('phoneValue')?.value?.trim();
      if (!emailVal && !phoneVal) {
        this.phoneEmailError.set('Please enter at least one: Email or Phone number.');
        this.submissionStatus.set('error');
        setTimeout(() => this.submissionStatus.set('idle'), 3000);
        return;
      } else {
        this.phoneEmailError.set(null); // Clear error if valid
      }
    }

    // Skip validation check for edit mode
    if (!this.isEditing && firstMember && firstMember.invalid && this.savedMembers.length === 0) {
      // If there's invalid data in the form and no saved members, rely on field-level highlighting
      this.submissionStatus.set('error');
      setTimeout(() => {
        this.submissionStatus.set('idle');
      }, 3000);
      return;
    }

    // Mark everything as touched to show errors
    this.teamMembersForm.markAllAsTouched();
    this.cdr.detectChanges();

    // Check if parking is selected when in parking tab
    if (this.isParkingAccess() && this.parkingFormControl?.invalid) {
      this.submissionStatus.set('error');
      setTimeout(() => this.submissionStatus.set('idle'), 3000);
      return;
    }

    // If in EDIT mode, submit directly without using savedMembers array
    if (this.isEditing && this.editingMemberId) {
      // Get current member data from form - use getRawValue to include disabled fields
      const currentMember = this.teamMembers.at(0) as FormGroup;
      const memberData = currentMember.getRawValue();

      // Set success status
      this.submissionStatus.set('success');
      // Notification will be shown in submitMembersToAPI

      // Submit single member for update
      this.submitMembersToAPI([memberData]);

      return // Exit early for edit mode
    }


    // Prepare members for API submission
    let membersToSubmit = [...this.savedMembers];

    // CREATE MODE: Check if there's current form data to include
    if (this.canSaveMember()) {
      // Validate the latest member form
      const currentMember = this.teamMembers.at(0) as FormGroup;
      if (currentMember.invalid) {
        this.submissionStatus.set('error');
        setTimeout(() => this.submissionStatus.set('idle'), 3000);
        return;
      }

      // Get data from form but DO NOT clear it or save to preview list yet
      const currentMemberData = this.getMemberDataFromForm();
      if (currentMemberData) {
        membersToSubmit.push(currentMemberData);
      }
    }

    // Check if we have any members to submit
    if (membersToSubmit.length === 0) {
      this.notificationService.showError('Please add at least one team member', 3000);
      this.submissionStatus.set('error');
      setTimeout(() => {
        this.submissionStatus.set('idle');
      }, 3000);
      return;
    }

    // Set submitting status
    this.submissionStatus.set('submitting');
    // Notification will be shown in submitMembersToAPI

    // Call API to register each member
    this.submitMembersToAPI(membersToSubmit);
  }

  // Submit members to API - Create all members as a team
  private submitMembersToAPI(members: any[]): void {
    console.log('submitMembersToAPI called');
    console.log('Edit Mode:', this.isEditing, 'Editing Member ID:', this.editingMemberId);
    console.log('Members count:', members.length);
    console.log('Members data:', members);

    // Get the selected role and parking for all members
    let selectedRole = this.teamMembersForm.get('role')?.value;
    const selectedParking = this.parkingFormControl.value;

    // If form control doesn't have the role, use the stored value
    if (!selectedRole && this.selectedRole) {
      selectedRole = this.selectedRole;
    }

    // Get role ID for all members
    let roleId = this.getRoleIdFromSelection(selectedRole);

    // Validate that we have a role ID
    if (!roleId) {
      this.notificationService.showError('Please select a valid role', 3000);
      this.submissionStatus.set('error');
      setTimeout(() => {
        this.submissionStatus.set('idle');
      }, 3000);
      return;
    }

    // Get role type name in uppercase (e.g. ADMIN, OPERATOR, VENDOR, etc)
    const roleType = (selectedRole?.name || '').trim().toUpperCase();

    // Get parking IDs for all members
    let parkingIds: string[] = [];
    if (Array.isArray(selectedParking) && selectedParking.length > 0) {
      parkingIds = selectedParking.map(p => p.id);
    } else {
      const storageParkingId = this.getParkingIdFromLocalStorage();
      if (storageParkingId) {
        parkingIds = [storageParkingId];
      }
    }

    // In parking-manager mode (parking creation flow), always use parking from localStorage
    if (this.stepType === 'parking-manager') {
      const parkingIdFromStorage = this.getParkingIdFromLocalStorage();
      if (parkingIdFromStorage && parkingIdFromStorage.trim() !== '') {
        parkingIds = [parkingIdFromStorage];
      }
      if (parkingIds.length === 0 && this.parkingFormControl.value && Array.isArray(this.parkingFormControl.value) && this.parkingFormControl.value.length > 0) {
        parkingIds = this.parkingFormControl.value.map(p => p.id);
      }
    }

    // Get selected service access (vehicle and passes) from All Services module
    let vehicleAccess = false;
    let passesAccess = false;
    const allServicesModule = this.modulesData.find((m: any) => m.id === 'all_services');
    if (allServicesModule && allServicesModule.childModules) {
      const vehicleControl = this.getChildModuleControl('all_services', 'vehicle_parking');
      const passesControl = this.getChildModuleControl('all_services', 'parking_passes');
      vehicleAccess = vehicleControl.value === true;
      passesAccess = passesControl.value === true;
    }

    // Get selected facility IDs from All Facilities module.
    // Parent checkbox is only a select-all UI control; the payload should contain facility IDs, not "all".
    const selectedFacilityIds: string[] = [];
    const allFacilitiesModule = this.modulesData.find((m: any) => m.id === 'All Facilities');
    if (allFacilitiesModule && allFacilitiesModule.childModules) {
      allFacilitiesModule.childModules.forEach((facility: any) => {
        const control = this.getChildModuleControl('All Facilities', facility.id);
        if (control.value === true) {
          selectedFacilityIds.push(facility.id);
        }
      });
    }

    // Check if we're in edit mode (updating single member)
    if (this.editingMemberId && members.length === 1) {
      // Edit mode - use PUT API
      const member = members[0];
      const userData = {
        id: this.editingMemberId,
        email: this.getEmailString(member.emailValue),
        name: member.nameValue,
        password: member.passwordValue || undefined, // Optional password
        phone: member.phoneValue,
        role_id: roleId,
        parkingIds: this.isOrganizationAccess() ? [] : parkingIds,
        org_id: this.apiService.getCurrentOrganizationId() || '',
        timeStamp: new Date().toISOString(),
        service_access: [{
          vehicle: vehicleAccess,
          passes: passesAccess,
          facility: selectedFacilityIds
        }],
      };

      this.apiService.updateUser(userData as any).subscribe({
        next: (response) => {
          this.submissionStatus.set('success');
          setTimeout(() => this.submissionStatus.set('idle'), 3000);
          this.apiService.clearRequestCache();
          this.teamMembersCreated.emit(response);

          this.resetAllFormValues() // Clear everything on success

          // In parking-manager mode, close drawer after a short delay to allow navigation
          if (this.stepType === 'parking-manager') {
            setTimeout(() => {
              this.closeDrawer();
            }, 300);
          } else {
            // In non-parking-manager mode, close drawer after longer delay
            setTimeout(() => {
              this.closeDrawer();
            }, 1000);
          }
        },
        error: (error) => {
          // Pass the email context if we are updating a single member
          const memberEmail = members.length === 1 ? this.getEmailString(members[0].emailValue) : undefined;
          this.handleApiError(error, memberEmail);
          this.submissionStatus.set('error');
          setTimeout(() => {
            this.submissionStatus.set('idle');
          }, 3000);
        }
      });
      return;
    }

    // Parking creation flow: parking is not active yet — store invite locally and send on Go Live
    if (this.stepType === 'parking-manager' && !this.editingMemberId) {
      this.deferParkingManagerInvite(
        members,
        parkingIds,
        roleId,
        roleType,
        vehicleAccess,
        passesAccess,
        selectedFacilityIds
      );
      return;
    }

    // Create mode - Invite each member using the new /user/add-to-team API
    const requests = members.map(member => {
      const email = this.getEmailString(member.emailValue);
      const rawPhone = member.phoneValue || '';
      const phone = rawPhone ? rawPhone.replace(/\s+/g, '') : '';

      // Look up existing user from existence check results
      const emailKey = String(email || '').trim().toLowerCase();
      const phoneKey = String(rawPhone || '').trim().toLowerCase();
      const existingUser = this.existingUsersMap.get(emailKey) || this.existingUsersMap.get(phoneKey);

      const isExisting = !!existingUser;
      const userId = existingUser ? existingUser.id : '';

      const memberParkingIds = this.isOrganizationAccess()
        ? []
        : (this.stepType === 'parking-manager'
          ? parkingIds
          : (member.parking?.id ? [member.parking.id] : parkingIds));

      // --- Per-member permissions ---
      // Use permissions saved at the time of "Add Another" click (member.permissions),
      // falling back to current modulesForm only for the last/current member.
      let memberVehicleAccess = vehicleAccess;
      let memberPassesAccess = passesAccess;
      let memberFacilityIds = selectedFacilityIds;

      if (member.permissions && Array.isArray(member.permissions)) {
        // permissions is an array of { moduleId, childId } objects saved from getSelectedPermissions()
        memberVehicleAccess = member.permissions.some(
          (p: any) => p.moduleId === 'all_services' && p.childId === 'vehicle_parking'
        );
        memberPassesAccess = member.permissions.some(
          (p: any) => p.moduleId === 'all_services' && p.childId === 'parking_passes'
        );
        memberFacilityIds = member.permissions
          .filter((p: any) => p.moduleId === 'All Facilities' && p.childId)
          .map((p: any) => p.childId);
      }

      const payload = {
        isExistingUser: isExisting,
        user_level_access: (this.isOrganizationAccess() && this.stepType !== 'parking-manager') ? 'organization' : 'parking',
        userData: {
          id: userId || undefined,
          name: member.nameValue,
          email: email || undefined,
          phone: phone || undefined,
          type: (selectedRole?.type || roleType).toLowerCase(),
          password: isExisting ? undefined : (member.passwordValue || undefined),
          org_id: this.apiService.getCurrentOrganizationId() || '',
          role_id: roleId,
          service_access: (this.isOrganizationAccess() && this.stepType !== 'parking-manager') ? undefined : [{
            vehicle: memberVehicleAccess,
            passes: memberPassesAccess,
            facility: memberFacilityIds
          }],
          parkingIds: memberParkingIds
        }
      };

      return this.apiService.inviteTeamMember(this.cleanPayload(payload));
    });

    console.log('Attempting to invite', requests.length, 'team member(s) via /user/add-to-team');

    this.submissionStatus.set('submitting');

    // Do NOT activate parking during drawer invite in parking creation flow.
    // Activation & manager assignment will happen at Go Live time.
    const tempActivate$ = of(null);

    tempActivate$.pipe(
      switchMap(() => forkJoin(requests))
    ).subscribe({
      next: (responses) => {
        console.log('Team member(s) invited successfully:', responses);
        this.submissionStatus.set('success');
        setTimeout(() => this.submissionStatus.set('idle'), 3000);
        this.notificationService.showSuccess(`${members.length} team member(s) invited successfully!`, 3000);

        this.resetAllFormValues() // Clear everything on success

        const latestResponse = responses.length > 0 ? responses[responses.length - 1] : null;
        // When data is a plain string (UUID), treat apiMember as null to avoid spreading a string
        const apiMember = (latestResponse?.data && typeof latestResponse.data !== 'string')
          ? latestResponse.data
          : (latestResponse && typeof latestResponse !== 'string' ? latestResponse : null);

        // Build member data from the submitted form data (reliable name/email/phone)
        // API response may not always contain name field
        const lastMember = members[members.length - 1];

        // Extract user id from all possible response fields
        // NOTE: /user/add-to-team returns { data: "<uuid-string>" } — handle string data directly
        const resolvedId =
          (typeof latestResponse?.data === 'string' ? latestResponse.data : '') ||
          apiMember?.id ||
          apiMember?.user_id ||
          apiMember?.userId ||
          apiMember?.data?.id ||
          apiMember?.data?.user_id ||
          apiMember?.data?.userId ||
          (latestResponse as any)?.data?.id ||
          (latestResponse as any)?.data?.user_id ||
          '';

        const latestMember = {
          ...(apiMember || {}),
          name: lastMember?.nameValue || apiMember?.name || apiMember?.fullName || '',
          email: lastMember?.emailValue || apiMember?.email || '',
          phone: lastMember?.phoneValue || apiMember?.phone || '',
          id: resolvedId,
          user_id: resolvedId,
        };

        // Persist invited manager ID immediately so any component (review-parking, parkings)
        // can use it for manager assignment on Go Live — regardless of in-memory state
        if (resolvedId) {
          localStorage.setItem('parking_invited_manager_id', resolvedId);
          console.log('[Drawer] Saved invited manager ID to localStorage:', resolvedId);
        }

        this.apiService.clearRequestCache();
        this.teamMembersCreated.emit(latestMember);

        // Close drawer after successful creation
        if (this.stepType === 'parking-manager') {
          setTimeout(() => {
            this.closeDrawer();
          }, 300);
        } else {
          setTimeout(() => {
            this.closeDrawer();
          }, 1000);
        }
      },
      error: (error) => {
        console.error('Failed to invite team member(s):', error);
        this.handleApiError(error);
        this.submissionStatus.set('error');
        setTimeout(() => this.submissionStatus.set('idle'), 3000);
      }
    });
  }

  /**
   * Parking creation wizard: parking is inactive until Go Live, so store the invite
   * locally and send it after activation.
   */
  private deferParkingManagerInvite(
    members: any[],
    parkingIds: string[],
    roleId: string,
    roleType: string,
    vehicleAccess: boolean,
    passesAccess: boolean,
    selectedFacilityIds: string[]
  ): void {
    const parkingId = parkingIds[0] || this.getParkingIdFromLocalStorage();
    if (!parkingId) {
      this.notificationService.showError('Parking ID not found. Please complete earlier steps.', 3000);
      this.submissionStatus.set('error');
      setTimeout(() => this.submissionStatus.set('idle'), 3000);
      return;
    }

    const member = members[members.length - 1];
    const email = this.getEmailString(member.emailValue);
    const rawPhone = member.phoneValue || '';
    const phone = rawPhone ? rawPhone.replace(/\s+/g, '') : '';
    const emailKey = String(email || '').trim().toLowerCase();
    const phoneKey = String(rawPhone || '').trim().toLowerCase();
    const existingUser = this.existingUsersMap.get(emailKey) || this.existingUsersMap.get(phoneKey);
    const isExisting = !!existingUser;
    const userId = existingUser ? existingUser.id : '';

    const payload = {
      isExistingUser: isExisting,
      user_level_access: 'parking',
      userData: {
        id: userId || undefined,
        name: member.nameValue,
        email: email || undefined,
        phone: phone || undefined,
        type: roleType.toLowerCase(),
        password: isExisting ? undefined : (member.passwordValue || undefined),
        org_id: this.apiService.getCurrentOrganizationId() || '',
        role_id: roleId,
        service_access: [{
          vehicle: vehicleAccess,
          passes: passesAccess,
          facility: selectedFacilityIds
        }],
        parkingIds: parkingIds
      }
    };

    localStorage.setItem('parking_pending_team_invite', JSON.stringify({
      parkingId,
      payload: this.cleanPayload(payload),
      display: {
        name: member.nameValue,
        email,
        phone,
        roleType,
        isExisting,
        userId: userId || null
      },
      savedAt: new Date().toISOString()
    }));

    this.submissionStatus.set('success');
    setTimeout(() => this.submissionStatus.set('idle'), 3000);

    this.resetAllFormValues();

    const latestMember = {
      name: member.nameValue,
      email,
      phone,
      id: userId || '',
      user_id: userId || '',
      userId: userId || '',
      pendingInvite: true,
      roleType,
      role: { name: roleType }
    };

    if (userId) {
      localStorage.setItem('parking_invited_manager_id', userId);
    }

    this.teamMembersCreated.emit(latestMember);

    setTimeout(() => this.closeDrawer(), 300);
  }

  /**
   * Helper to strip null, undefined, and empty string properties from payloads
   */
  private cleanPayload(obj: any): any {
    const clean = (item: any): any => {
      if (item === null || item === undefined) return undefined;
      if (typeof item === 'string' && item.trim() === '') return undefined;
      if (Array.isArray(item)) {
        const arr = item.map(clean).filter(v => v !== undefined);
        return arr.length > 0 ? arr : undefined;
      }
      if (typeof item === 'object') {
        const res: any = {};
        for (const key in item) {
          if (item.hasOwnProperty(key)) {
            const val = clean(item[key]);
            if (val !== undefined) {
              res[key] = val;
            }
          }
        }
        return Object.keys(res).length > 0 ? res : undefined;
      }
      return item;
    };
    return clean(obj);
  }





  // Map role name to API type
  private mapRoleToType(roleName: string): string {
    const roleMap: { [key: string]: string } = {
      'Admin Role': 'admin',
      'Manager / Vendor Role': 'manager',
      'Operator Role': 'operator',
      'Accountant Role': 'accountant',
      'Viewer Role': 'viewer',
      'Custom Role': 'user'
    };
    return roleMap[roleName] || 'user';
  }

  // Method to check if a role should be disabled
  protected isRoleDisabled(roleName: string): boolean {
    // Deprecated by item-level disabling below. Kept for backward compatibility where only a name is passed.
    const filtered = this.getFilteredRoleList();
    return !filtered.some(r => this.roleNamesEqual(r.name, roleName));
  }

  // Method to check if a specific role item should be disabled
  protected isRoleItemDisabled(role: any): boolean {
    const filtered = this.getFilteredRoleList();
    return !filtered.some(r => this.roleMatcher(r, role));
  }

  // Method to get filtered role list using computed signal for high performance rendering
  protected readonly getFilteredRoleList = computed(() => {
    this.open() // Track drawer state
    const roles = this.roleListSignal();
    const isParkingAccess = this.isParkingAccess();
    const selectedParkings = this.selectedParkingsSignal() || this.parkingFormControl?.value;

    // If we are editing an Admin, only show the Admin role
    if (this.isEditing && this.selectedRole && (this.roleNamesEqual(this.selectedRole.name, 'Admin') || this.roleNamesEqual(this.selectedRole.name, 'Super Admin'))) {
      const adminRoles = (roles || []).filter((r: any) => this.roleNamesEqual(r.name, 'Admin') || this.roleNamesEqual(r.name, 'Super Admin'));
      if (adminRoles.length > 0) {
        return adminRoles;
      }
    }

    // In parking creation wizard ("Assign Manager/Vendor" step), only show Manager and Vendor roles
    if (this.stepType === 'parking-manager') {
      return (roles || []).filter(r =>
        this.roleNamesEqual(r.name, 'Manager') || this.roleNamesEqual(r.name, 'Vendor')
      );
    }

    const currentTab = isParkingAccess ? 'parking' : 'organization';

    // Helper to exclude roles by names (exact match only, not synonyms)
    const excludeByNames = (list: any[], excludeNames: string[]) => {
      return list.filter(r => {
        const roleName = (r.name || '').trim().toLowerCase();
        return !excludeNames.some(excludeName => {
          const excludeNameLower = excludeName.trim().toLowerCase();
          return roleName === excludeNameLower;
        });
      });
    };

    const isAdminUser = this.isAdminUser();
    const currentRole = isAdminUser
      ? 'admin'
      : (this.roleService.getCurrentRole() || '').toLowerCase();

    let excludeList = ['Custom', 'Admin', 'Super Admin'];
    let filteredRoles = excludeByNames(roles || [], excludeList);

    const isOrganizationTab = currentTab === 'organization';
    const isParkingTab = currentTab === 'parking';
    const isParkingLevel = !!selectedParkings || !!this.getParkingIdFromLocalStorage();

    const includeByNames = (names: string[]) => {
      return filteredRoles.filter(r => names.some(n => this.roleNamesEqual(r.name, n)));
    };

    if (isAdminUser) {
      if (isParkingLevel || isParkingTab) {
        let res = includeByNames(['Vendor', 'Manager', 'Accountant', 'Viewer', 'Operator']);
        if (isOrganizationTab) {
          res = excludeByNames(res, ['Vendor', 'Operator']);
        }
        return res;
      }
      let res = includeByNames(['Manager', 'Accountant', 'Viewer']);
      if (isOrganizationTab) {
        res = excludeByNames(res, ['Vendor', 'Operator']);
      }
      return res;
    }

    if (currentRole === 'manager' || currentRole === 'vendor') {
      let res = includeByNames(['Operator', 'Accountant', 'Viewer']);
      if (isOrganizationTab) {
        res = excludeByNames(res, ['Vendor', 'Operator']);
      }
      return res;
    }

    if (isParkingTab) {
      let res = filteredRoles;
      const hasOperator = res.some(r => this.roleNamesEqual(r.name, 'Operator'));
      if (!hasOperator) {
        const operatorRole = roles.find(r => this.roleNamesEqual(r.name, 'Operator'));
        if (operatorRole) {
          res = [...res, operatorRole];
        }
      }
      return res;
    }

    if (isOrganizationTab) {
      return excludeByNames(filteredRoles, ['Vendor', 'Operator']);
    }

    return filteredRoles;
  });

  // Compare role display names allowing common synonyms
  private roleNamesEqual(a: string, b: string): boolean {
    if (!a || !b) return false;
    const an = String(a).trim().toLowerCase();
    const bn = String(b).trim().toLowerCase();
    if (an === bn) return true;
    // Manager ~ Vendor synonym
    const isManager = (v: string) => v === 'manager' || v === 'vendor' || v === 'manager / vendor role';
    if (isManager(an) && isManager(bn)) return true;
    // Allow matching against localized roles if necessary, currently basic compare
    return false;
  }

  // Compare two role objects for TuiIdentityMatcher
  protected roleMatcher: TuiIdentityMatcher<any> = (a: any, b: any): boolean => {
    if (!a || !b) return false;
    if (a === b) return true;
    if (a.id && b.id) return a.id === b.id;
    return this.roleNamesEqual(a.name, b.name);
  }

  // Method to check if currently selected role is Admin or Super Admin using computed signal
  protected readonly isRoleAdminSelected = computed(() => {
    this.open();
    const role = this.roleValueSignal() || this.teamMembersForm?.get('role')?.value || this.selectedRole;
    if (!role) return false;
    const name = (role.name || role.role_name || '').toLowerCase();
    return name === 'admin' || name === 'super admin';
  });

  // Method to handle role selection when checkbox is clicked
  protected onRoleSelectionChange(selectedRole: any): void {

    // Store the selected role
    this.selectedRole = selectedRole;
    this.roleValueSignal.set(selectedRole);

    // Also update all current member forms with this role
    this.teamMembers.controls.forEach((memberForm, index) => {
      memberForm.patchValue({
        role: selectedRole
      });
    });
  }

  // Method to check if a role is currently selected
  protected isRoleSelected(role: any): boolean {
    const currentRole = this.teamMembersForm.get('role')?.value;
    return currentRole && currentRole.id === role.id;
  }

  // Get role ID by name from roleList
  private getRoleIdByName(roleName: string): string {
    const role = this.roleList.find(r => r.name === roleName);
    return role?.id || '';
  }

  // Get role ID from selected role
  private getRoleIdFromSelection(selectedRole: any): string {
    if (!selectedRole) {

      return '';
    }


    // First try to get ID directly from selected role
    if (selectedRole.id) {

      return selectedRole.id;
    }

    // If no ID, try to find by name in roleList
    if (selectedRole.name) {
      const foundRole = this.roleList.find(role => role.name === selectedRole.name);
      if (foundRole && foundRole.id) {

        return foundRole.id;
      }
    }

    return '';
  }



  // Get parking ID from localStorage
  private getParkingIdFromLocalStorage(): string {
    // Priority 1: Use explicitly provided parkingId input
    if (this.parkingId && this.parkingId.trim() !== '') {
      return this.parkingId;
    }

    // Priority 2: Use standardized 'parking_id' from localStorage/sessionStorage (matching ApiService)
    const parkingIdSnake = localStorage.getItem('parking_id') || sessionStorage.getItem('parking_id');
    if (parkingIdSnake && parkingIdSnake.trim() !== '') {
      return parkingIdSnake;
    }

    // Priority 3: Try vendorContext.parkingId
    const vendorContext = sessionStorage.getItem('vendorContext');
    if (vendorContext) {
      try {
        const vendorData = JSON.parse(vendorContext);
        if (vendorData.parkingId) {
          return vendorData.parkingId;
        }
      } catch (e) { }
    }

    // Fallback: Try camelCase 'parkingId'
    const parkingIdCamel = localStorage.getItem('parkingId') || sessionStorage.getItem('parkingId');
    if (parkingIdCamel && parkingIdCamel.trim() !== '') {
      return parkingIdCamel;
    }

    return "";
  }

  // Check if parking ID already exists (from localStorage)
  protected readonly hasExistingParkingId = computed(() => {
    this.open();
    const parkingId = this.getParkingIdFromLocalStorage();
    return !!parkingId && parkingId.trim() !== '';
  });

  // Check if current user is admin using computed signal
  protected readonly isAdminUser = computed(() => {
    this.open();
    const currentUser = this.apiService.getCurrentUser();
    if (!currentUser) return false;

    // Use RoleService's normalizeRole method for consistent role determination
    const normalizedRole = this.roleService.normalizeRole({
      roleId: currentUser.role_id,
      roleName: currentUser.role?.name,
      type: currentUser.type,
      email: currentUser.email
    });

    return normalizedRole === 'admin' || normalizedRole === 'super admin';
  });

  protected isOrganizationTeamPage(): boolean {
    const url = this.router.url;
    // Matches /organization/:id/settings/team but explicitly excludes /parking/ to avoid sub-pages
    return url.includes('/organization/') && url.includes('/settings/team') && !url.includes('/parking/');
  }

  protected readonly shouldShowTabs = computed(() => {
    // Segmented control removed as requested
    return false;
  });

  protected readonly isInParkingView = computed(() => {
    this.open();
    // If a parking ID is provided as an input or we're in parking-manager mode, lock to Parking context (hide tabs)
    if (this.stepType === 'parking-manager' || (this.parkingId && typeof this.parkingId === 'string' && this.parkingId.trim() !== '')) {
      return true;
    }

    // Check if current URL is under a specific parking context
    if (this.router.url.includes('/parking/')) {
      return true;
    }

    // Specifically check if we are on the Organization Team page
    if (this.isOrganizationTeamPage()) {
      return false;
    }

    // For Admins, we ignore "ambient" parking context (localStorage)
    // to allow them to default to Org view unless explicitly locked to a parking.
    if (this.isAdminUser()) {
      return false;
    }

    // For non-admins (Managers, Vendors), they are always restricted to their parking context.
    return this.hasExistingParkingId();
  });


  // Check if parking list should be shown (only when roles > 3, i.e., admin in parking-level context)
  protected shouldShowParkingList(): boolean {
    const filteredRoles = this.getFilteredRoleList();
    // Show parking list only when there are more than 3 roles (admin in parking-level context)
    // Hide when there are 3 or fewer roles (manager/vendor view or admin in org-level)
    return filteredRoles.length > 3;
  }

  // Alternative method to check admin status using RoleService
  protected isAdminUserViaRoleService(): boolean {
    try {
      // Get current role from RoleService
      const currentRole = this.roleService.getCurrentRole();


      // Check if current role is admin or super admin
      const isAdmin = currentRole === 'admin' || currentRole === 'super admin';


      return isAdmin;
    } catch (error) {

      return false;
    }
  }

  // Debug method - call this from browser console to test admin status
  public debugAdminStatus(): void {


    // Check current user
    const currentUser = this.apiService.getCurrentUser();


    // Check localStorage
    const userInfo = localStorage.getItem('user_info');

    const currentRole = sessionStorage.getItem('current_role');


    // Test both methods

    // Test RoleService methods


    if (currentUser) {
      const normalizedRole = this.roleService.normalizeRole({
        roleId: currentUser.role_id,
        roleName: currentUser.role?.name,
        type: currentUser.type,
        email: currentUser.email
      });

    }


  }






  // Handle parking selection change
  protected onParkingSelectionChange(): void {
    // Parking selection change handler
  }
  /**
   * Generates a strong random password and populates the password field
   */
  protected generatePassword(index: number = 0): void {
    const length = 12;
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+~`|}{[]:;?><,./-=";
    let password = "";
    for (let i = 0, n = charset.length; i < length; ++i) {
      password += charset.charAt(Math.floor(Math.random() * n));
    }

    const group = this.teamMembers.at(index) as FormGroup;
    if (group) {
      group.get('passwordValue')?.setValue(password);
      group.get('passwordValue')?.markAsDirty();
      group.get('passwordValue')?.markAsTouched();
    }

    this.cdr.detectChanges();
    this.notificationService.showInfo('Secure password generated!');
  }

  // Handle API error messages
  private handleApiError(error: any, memberEmail?: string): void {
    let errorMessage = 'An error occurred while creating team member(s).';

    // Extract error message from different possible locations
    // Handle both raw HttpErrorResponse and transformed Error objects from ApiService
    if (error && error.error) {
      if (Array.isArray(error.error.message)) {
        errorMessage = error.error.message.join('. ');
      } else if (Array.isArray(error.error.errors)) {
        errorMessage = error.error.errors.map((e: any) => typeof e === 'string' ? e : (e.message || JSON.stringify(e))).join('. ');
      } else if (Array.isArray(error.error.details)) {
        errorMessage = error.error.details.map((e: any) => typeof e === 'string' ? e : (e.message || JSON.stringify(e))).join('. ');
      } else if (error.error.message) {
        errorMessage = error.error.message;
      } else if (error.error.error) {
        errorMessage = error.error.error;
      }
    } else if (error && error.message) {
      errorMessage = error.message;
    } else if (error && typeof error === 'string') {
      errorMessage = error;
    } else if (error && error.statusText) {
      errorMessage = error.statusText;
    }

    // Clean up specific API error message format if present
    if (errorMessage.includes('Error message: ')) {
      errorMessage = errorMessage.split('Error message: ')[1];
    }

    // Append instruction to "already exists" message to improve clarity
    const lowerMsg = errorMessage.toLowerCase();
    if (lowerMsg.includes('already exists') || lowerMsg.includes('duplicate')) {
      const instruction = " You can update this member's access from the Team table.";
      if (!errorMessage.includes(instruction)) {
        // Fix typo in original message if it exists, then append instruction
        errorMessage = errorMessage.replace('Member tabel', 'Member table');
        if (!errorMessage.endsWith('.')) errorMessage += '.';
        errorMessage += instruction;
      }
    }

    let mapped = false;

    // 1. Try to map to a specific form control if memberEmail is provided
    if (memberEmail) {
      const emailToMatch = memberEmail.toLowerCase().trim();

      // Look in the active team members form array (usually only index 0 in this component)
      for (let i = 0; i < this.teamMembers.length; i++) {
        const group = this.teamMembers.at(i) as FormGroup;
        const groupEmail = this.getEmailString(group.get('emailValue')?.value).toLowerCase().trim();

        if (groupEmail === emailToMatch) {
          const lowerMsg = errorMessage.toLowerCase();

          // Check for phone specifically to avoid it being caught by general existence checks
          if (lowerMsg.includes('phone') || lowerMsg.includes('mobile')) {
            group.get('phoneValue')?.setErrors({ custom: errorMessage });
            mapped = true;
          } else if (lowerMsg.includes('email')) {
            group.get('emailValue')?.setErrors({ custom: errorMessage });
            mapped = true;
          } else if (lowerMsg.includes('exists') || lowerMsg.includes('duplicate')) {
            // Default to email if it just says "exists" without specifying, 
            // but check if phone might be the cause
            group.get('emailValue')?.setErrors({ custom: errorMessage });
            mapped = true;
          } else if (lowerMsg.includes('password')) {
            group.get('passwordValue')?.setErrors({ custom: errorMessage });
            mapped = true;
          } else if (lowerMsg.includes('name')) {
            group.get('nameValue')?.setErrors({ custom: errorMessage });
            mapped = true;
          }
          break;
        }
      }

      // Also look in savedMembers to mark failed entries in the preview list
      if (!mapped) {
        for (let i = 0; i < this.savedMembers.length; i++) {
          const savedEmail = this.getEmailString(this.savedMembers[i].emailValue).toLowerCase().trim();
          if (savedEmail === emailToMatch) {
            this.savedMembers[i].error = errorMessage;
            mapped = true;
            break;
          }
        }
      }
    }

    // 2. Map parking errors to the parking form control
    if (errorMessage.toLowerCase().includes('parking')) {
      this.parkingFormControl.setErrors({ custom: errorMessage });
      mapped = true;
    }

    // Always show the message in the global notification signal as requested
    this.apiErrorMessage.set(errorMessage);

    // Also show toast and map to fields if possible
    if (!mapped) {
      this.notificationService.showError(errorMessage, 7000);
    }

    // Force change detection to show the errors in the UI
    this.cdr.detectChanges();

    // Auto-scroll to top so the user can see the error message
    this.scrollToTop();
  }

  /**
   * Smoothly scrolls the drawer content to the top or to the error message
   */
  private scrollToTop(): void {
    // Small delay to allow *ngIf to render the notification and DOM to settle
    setTimeout(() => {
      // Re-trigger change detection inside the timeout to ensure ViewChild is populated
      this.cdr.detectChanges();

      // Method 1: Use the ViewChild if available
      if (this.errorNotification && this.errorNotification.nativeElement) {
        this.errorNotification.nativeElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
        return;
      }

      // Method 2: Fallback to direct DOM selection
      const errorElement = document.getElementById('errorNotification');
      if (errorElement) {
        errorElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
        return;
      }

      // Method 3: Fallback to scrolling all potential containers to top
      const containers = document.querySelectorAll('.tui-scrollbar, tui-drawer, [tuiPopup], .t-content');
      containers.forEach(container => {
        container.scrollTop = 0;
      });
    }, 250);
  }



  // Prevent form submission on Enter key press
  protected onKeyPress(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      const target = event.target as HTMLElement;
      const isMultiline = target?.tagName === 'TEXTAREA' || target?.isContentEditable;
      if (!isMultiline) {
        event.preventDefault();
      }
    }
  }

  // Handle tab change
  protected onTabChange(tab: 'organization' | 'parking'): void {
    this.activeTab.set(tab);
    // Sync dropdown value if changed from outside (e.g. initial load or tab click)
    this.selectedAccessType = tab === 'organization' ? 'Organization Access' : 'Parking Access';

    // Clear any previous error states from status signals
    this.submissionStatus.set('idle');

    // Reset role when tab changes to avoid carrying over invalid role selections between tabs
    this.teamMembersForm.patchValue({ role: null });
    this.selectedRole = null;
    this._cachedFilteredRoles = null;

    // Set validation based on tab
    if (tab === 'parking') {
      this.parkingFormControl.setValidators([Validators.required]);
    } else {
      this.parkingFormControl.clearValidators();
    }
    this.parkingFormControl.updateValueAndValidity();

    // Force UI update
    this.cdr.detectChanges();
  }
}


